import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Mail,
  MapPin,
  Package,
  ShoppingCart,
  Loader,
  AlertTriangle,
  X,
  CheckCircle,
  MessageSquare,
  Plus,
  Minus,
  Edit,
  Trash2,
  Shield,
  TrendingUp,
  Heart,
  AlertCircle,
  Flag,
  Clock,
  Calendar,
  DollarSign,
  Image as ImageIcon,
  Tag,
  Gift,
} from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";
import { formatTimestampFull } from "../../utils/dateFormatter";
import { formatPhoneWithCountryCode } from "../../utils/phoneFormatter";
import { getLeadDisplayAddress } from "../../utils/leadAddress";
import { useLanguage } from "../../contexts/LanguageContext";
// BEX address slice removed for Greek-CC (Greece uses ACS only)
// import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomTimePicker from "../../components/CustomTimePicker";
// BEX types for address data
type Municipality = { id: number; name: string };
type Settlement = { id: number; name: string; municipalityId: number; zip?: string };
type Street = { id: number; name: string; settlementId: number };

interface LeadProcessingPageProps {
  leadId: string;
  onBack: () => void;
  onSave: () => void;
  currentUser: {
    id: string;
    username: string;
    email: string;
    role: string;
  };
  onCallEnd?: () => void;
  onTakeNextLead?: (excludeLeadId?: string) => Promise<void>; // Function to automatically take the next lead
}

interface LeadData {
  _id?: string;
  name: string;
  phone: string;
  email?: string;
  spokenName?: string;
  city?: string;
  area?: string;
  address?: string;
  addr?: string;
  index?: string;
  notes?: string;
  stage?: string;
  bexMatched?: boolean;
  bexZipCode?: string;
  bexMatchDetails?: any;
  matchingOrders?: any[];
  leadTask?: string;
  createdAt?: string;
  updatedAt?: string;
  // Extended fields
  preferredLanguage?: "SR" | "EN";
  preferredContactChannel?: "call" | "SMS" | "Viber" | "email";
  bestTimeToCall?: string;
  timezone?: string;
  marketingConsent?: "opt-in" | "opt-out";
  marketingConsentTimestamp?: string;
  marketingConsentSource?: string;
  contactPermissions?: {
    call?: boolean;
    sms?: boolean;
    viber?: boolean;
    email?: boolean;
  };
  doNotCall?: boolean;
  doNotCallReason?: string;
  source?: string;
  campaign?: string;
  leadStatus?: "new" | "contacted" | "qualified" | "interested" | "order_started" | "customer" | "repeat" | "inactive";
  firstContactDate?: string;
  lastContactDate?: string;
  assignedAgent?: string;
  assignedTeam?: string;
  nextAction?: string;
  followUpDate?: string;
  followUpNotes?: string;
  followUpPotentialProduct?: string;
  callOutcome?: string;
  orderCount?: number;
  totalSpent?: number;
  averageOrderValue?: number;
  lastOrderDate?: string;
  paymentMethodPreference?: string;
  interestedCategories?: string[];
  productsMentioned?: Array<{ name: string; reason?: string }>;
  budgetRange?: "low" | "medium" | "high";
  objections?: string[];
  mainQuestions?: string[];
  competitorsMentioned?: string[];
  complaintType?: string[];
  resolutionStatus?: string;
  satisfactionScore?: "positive" | "neutral" | "negative";
  deliveryConstraints?: string;
  courierPreference?: string;
  addressAccuracyNotes?: string;
  isDuplicate?: boolean;
  fraudRisk?: "low" | "medium" | "high";
  fraudRiskReason?: string;
  abusiveCommunication?: boolean;
  redFlags?: Array<{ type: string; reason: string; addedAt: string; addedBy: string }>;
  problematicReason?: string | null;
  operatorActions?: Array<{
    at: string | Date;
    action: 'end_call' | 'skip';
    completedBy?: { userId: string; username: string };
    callOutcome?: string;
    notInterestedReason?: string;
    callNotes?: string;
    callRating?: number;
    followUpDate?: string;
    followUpHours?: string;
    followUpNotes?: string;
    followUpPotentialProduct?: string;
  }>;
  lastOrderNeedsConfirmation?: boolean;
  lastOrderConfirmOnDate?: string;
  /** Set when operator rejected the order that needed confirmation. */
  lastOrderRejected?: boolean;
  /** Timestamp of the most recent end call (for matching call history entry to current state). */
  callCenterCompletedAt?: string | Date;
  /** Product the lead is interested in (from AlterCPA import). */
  interestedForProduct?: {
    productId: string;
    productName: string;
  } | null;
  /** Call history from external software (e.g. AlterCPA) with matched products. */
  externalHistory?: Array<{
    source: string;
    callDate: string | null;
    offername: string;
    matchedProduct: { productId: string; productName: string } | null;
    status?: number | null;
    statusLabel?: string | null;
    reason?: number | null;
    reasonLabel?: string | null;
  }>;
}

interface Product {
  id: string;
  name: string;
  price: number;
  currency: string;
  image?: string;
  sku?: string;
  quantity?: number; // Stock quantity (for single products)
  estimatedStock?: number; // Estimated stock (for bundle products)
  type?: "single" | "bundle";
  freeShipping?: boolean;
}

interface Offer {
  _id?: string;
  id?: string;
  name: string;
  type: "special" | "gift";
  description?: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  unlimited: boolean;
  availableProducts?: Array<{
    productId: string;
    productName: string;
    price: number;
  }>;
  maxPickCount?: number;
  specialPrice?: number;
  baseProducts?: Array<{
    productId: string;
    productName: string;
    type: "single" | "bundle";
  }>;
  giftItems?: Array<{
    productId: string;
    productName: string;
    quantity: number;
  }>;
  freeShipping?: boolean;
}

interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  currency: string;
  image?: string;
  discountNote?: string;
  isSpecialOffer?: boolean; // Flag to indicate this is a special offer item
  specialOfferId?: string; // ID of the special offer
  includedProducts?: Array<{ // Products included in the special offer
    productId: string;
    productName: string;
    image?: string; // Product image
  }>;
  freeShipping?: boolean; // From offer when item is special/gift offer
}

/** Not interested sub-reasons (required when callOutcome is not_interested). */
export type NotInterestedReason = "" | "no_money" | "still_has_product" | "product_doesnt_help" | "do_not_call_again" | "because_of_death" | "orders_for_someone_else" | "not_interested_not_interested" | "other";

interface CallNotes {
  notes: string;
  callRating: number;
  callOutcome: "" | "successful" | "successful_with_confirmation" | "no_answer" | "busy_line" | "wrong_number" | "unreachable" | "number_doesnt_exist" | "not_interested" | "callback" | "other";
  notInterestedReason: NotInterestedReason;
  followUpDate?: string;
  followUpHours?: string;
  followUpNotes?: string;
  followUpPotentialProduct?: string;
}

/** Ensure phone number starts with 0 (local format). */
function normalizePhoneToStartWithZero(phone: string | undefined): string {
  if (!phone || !phone.trim()) return phone || "";
  const t = phone.trim();
  if (t[0] === "0") return phone;
  if (/^[1-9]/.test(t)) return "0" + t;
  return phone;
}

/** Next working day in local date (tomorrow; Fri → Monday, Sat/Sun → Monday). YYYY-MM-DD. */
function getNextWorkingDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2); // Sat -> Mon
  if (day === 0) d.setDate(d.getDate() + 1); // Sun -> Mon
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dom = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dom}`;
}

/** Next working day after a given date (YYYY-MM-DD). Used for shipping date when order needs confirmation on confirmOnDate. */
function getNextWorkingDateAfter(dateYmd: string): string {
  if (!dateYmd || !/^\d{4}-\d{2}-\d{2}$/.test(dateYmd)) return getNextWorkingDate();
  const d = new Date(dateYmd + "T12:00:00");
  d.setDate(d.getDate() + 1);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2); // Sat -> Mon
  if (day === 0) d.setDate(d.getDate() + 1); // Sun -> Mon
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dom = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dom}`;
}

/** Set to true to show "When should the order be sent?" date picker (hidden for now while iterating). */
const SHOW_ORDER_SEND_DATE_PICKER = false;

/** True when send date is after the next working day → order is potential, must be confirmed on that date. */
function isPotentialOrderDate(requestedSendDate: string): boolean {
  if (!requestedSendDate || !/^\d{4}-\d{2}-\d{2}$/.test(requestedSendDate)) return false;
  return requestedSendDate > getNextWorkingDate();
}

/** Extract street name and address number from address string (used by BEX address logic). */
function parseAddressComponents(addressString: string): { street: string; number: string } {
  if (!addressString) return { street: "", number: "" };

  let street = addressString.trim();
  let number = "";

  const words = street.split(/\s+/);
  if (words.length >= 4) {
    for (let seqLength = Math.min(5, Math.floor(words.length / 2)); seqLength >= 1; seqLength--) {
      if (words.length < seqLength * 2) continue;
      const lastSequence = words.slice(-seqLength).join(" ");
      const beforeSequence = words.slice(0, -seqLength).join(" ");
      if (beforeSequence.includes(lastSequence)) {
        street = beforeSequence.trim();
        break;
      }
    }
  }

  const numberPatterns = [
    /\s+(br\.?|no\.?|#)\s*(\d+.*)$/i,
    /\s+(\d+\s*\/\s*\d+.*)$/i,
  ];
  for (const pattern of numberPatterns) {
    const match = street.match(pattern);
    if (match) {
      number = match[match.length - 1].trim();
      street = street.replace(pattern, "").trim();
      break;
    }
  }

  if (!number) {
    const trailingNumberPattern = /^(.+?)\s+(\d+)$/;
    const match = street.match(trailingNumberPattern);
    if (match) {
      const beforeNumber = match[1].trim();
      const potentialNumber = match[2];
      if (!beforeNumber.endsWith(".)") && !beforeNumber.match(/\)\s*$/)) {
        const lastWord = beforeNumber.split(/\s+/).pop() || "";
        if (!lastWord.endsWith(")")) {
          number = potentialNumber;
          street = beforeNumber;
        }
      }
    }
  }

  return { street: street.trim(), number: number.trim() };
}

/** Fuzzy match score 0–1: proportion of query chars found in target in order. Used for address autocomplete (90%+ only). */
function fuzzyAddressScore(query: string, target: string): number {
  const q = (query || "").trim().toLowerCase();
  const t = (target || "").trim().toLowerCase();
  if (!q) return 1;
  let matched = 0;
  for (let i = 0, j = 0; i < t.length && j < q.length; i++) {
    if (t[i] === q[j]) {
      matched++;
      j++;
    }
  }
  return matched / q.length;
}

const FUZZY_MIN_SCORE = 0.9;

const WEEKDAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Inline calendar for "When should the order be sent?" / "Confirm on date" – weekends shown with light gray background; green base for selected/hover. */
function SendDateCalendar({
  value,
  onChange,
  minDate,
  allowWeekends = false,
  allowSaturdayOnly = false,
}: {
  value: string;
  onChange: (yyyyMmDd: string) => void;
  minDate: Date;
  allowWeekends?: boolean;
  allowSaturdayOnly?: boolean;
}) {
  const minYmd = minDate.getFullYear() * 10000 + (minDate.getMonth() + 1) * 100 + minDate.getDate();
  const initialViewing =
    value && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? (() => {
          const [y, m] = value.split("-").map(Number);
          return new Date(y, m - 1, 1);
        })()
      : (() => {
          const d = new Date(minDate);
          d.setDate(1);
          return d;
        })();
  const [viewing, setViewing] = useState(initialViewing);
  useEffect(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m] = value.split("-").map(Number);
      setViewing(new Date(y, m - 1, 1));
    } else {
      const today = new Date();
      today.setDate(1);
      setViewing(today);
    }
  }, [value]);
  const year = viewing.getFullYear();
  const month = viewing.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = last.getDate();
  const cells: Array<{ date: Date; dayOfMonth: number; isWeekend: boolean; ymd: string }> = [];
  for (let i = 0; i < startPad; i++) {
    const d = new Date(year, month, -startPad + 1 + i);
    cells.push({
      date: d,
      dayOfMonth: d.getDate(),
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
      ymd: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    cells.push({
      date,
      dayOfMonth: d,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      ymd: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    });
  }
  const padEnd = (7 - (cells.length % 7)) % 7;
  for (let i = 0; i < padEnd; i++) {
    const d = new Date(year, month + 1, i + 1);
    cells.push({
      date: d,
      dayOfMonth: d.getDate(),
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
      ymd: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    });
  }
  const canGoPrev =
    viewing.getFullYear() > minDate.getFullYear() ||
    (viewing.getFullYear() === minDate.getFullYear() && viewing.getMonth() > minDate.getMonth());
  const prevMonth = () => {
    if (!canGoPrev) return;
    const d = new Date(viewing);
    d.setMonth(d.getMonth() - 1);
    setViewing(d);
  };
  const nextMonth = () => {
    const d = new Date(viewing);
    d.setMonth(d.getMonth() + 1);
    setViewing(d);
  };
  return (
    <div className="border border-gray-300 rounded-lg p-2 bg-white">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={prevMonth}
          className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-40"
          disabled={!canGoPrev}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium text-gray-900">
          {viewing.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </span>
        <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 text-gray-600">
          <ArrowLeft className="w-4 h-4 rotate-180" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
        {WEEKDAY_HEADERS.map((h) => (
          <div key={h} className="py-1 font-medium text-gray-500">
            {h}
          </div>
        ))}
        {cells.map(({ date, dayOfMonth, isWeekend, ymd }) => {
          const cellYmd = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
          const isPast = cellYmd < minYmd;
          const isSelected = value === ymd;
          const isSaturday = date.getDay() === 6;
          const isClickable = !isPast && (allowWeekends || !isWeekend || (allowSaturdayOnly && isSaturday));
          return (
            <button
              key={ymd + String(date.getMonth())}
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onChange(ymd)}
              style={!isClickable ? { pointerEvents: "none" } : undefined}
              className={[
                "w-8 h-8 rounded flex items-center justify-center mx-auto",
                isWeekend && !isClickable ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "",
                isPast && !isWeekend ? "text-gray-300 cursor-not-allowed" : "",
                isPast && isWeekend ? "text-gray-300 cursor-not-allowed" : "",
                isClickable ? "hover:bg-[#264a3e] hover:text-white" : "",
                isSelected ? "bg-[#264a3e] text-white" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {dayOfMonth}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Returns the next working day as Date (tomorrow, or next Monday if Fri/Sat/Sun). */
function getNextWorkingDateAsDate(): Date {
  const ymd = getNextWorkingDate();
  return new Date(ymd + "T12:00:00");
}

/** Date picker: input shows formatted date; calendar opens on click, closes on outside click or day select. Green base (#264a3e) for selected/hover. */
function SendDatePicker({
  value,
  onChange,
  minDate,
  allowWeekends = false,
  allowSaturdayOnly = false,
}: {
  value: string;
  onChange: (yyyyMmDd: string) => void;
  minDate: Date;
  allowWeekends?: boolean;
  allowSaturdayOnly?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // After calendar is displayed, scroll it into view (e.g. when picker is near bottom of viewport)
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      dropdownRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const displayText = value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(value + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        readOnly
        value={displayText}
        placeholder="Select date"
        onClick={() => setIsOpen((o) => !o)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:outline-none bg-white cursor-pointer"
      />
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute left-0 top-full mt-1 z-20 shadow-lg rounded-lg overflow-y-auto max-h-[85vh] border border-[#264a3e]/30 bg-white"
        >
          <SendDateCalendar
            value={value}
            onChange={(yyyyMmDd) => {
              onChange(yyyyMmDd);
              setIsOpen(false);
            }}
            minDate={minDate}
            allowWeekends={allowWeekends}
            allowSaturdayOnly={allowSaturdayOnly}
          />
        </div>
      )}
    </div>
  );
}

const LeadProcessingPage: React.FC<LeadProcessingPageProps> = ({
  leadId,
  onBack,
  onSave,
  currentUser,
  onCallEnd,
  onTakeNextLead,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productImagesMap, setProductImagesMap] = useState<Record<string, string>>({}); // productId -> image for interested items (includes out-of-stock)
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null); // Currently active offer for product selection
  const [selectedOfferProducts, setSelectedOfferProducts] = useState<string[]>([]); // For special offers - products selected for the active offer
  const [selectedOffers, setSelectedOffers] = useState<Map<string, { offer: Offer; products: string[] }>>(new Map()); // Track all selected special offers
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [shippingComment, setShippingComment] = useState("");
  const [requestedSendDate, setRequestedSendDate] = useState<string>(""); // YYYY-MM-DD, weekdays only
  const [callNotes, setCallNotes] = useState<CallNotes>({
    notes: "",
    callRating: 0,
    callOutcome: "",
    notInterestedReason: "",
  });
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [endCallModalFromCreateOrder, setEndCallModalFromCreateOrder] = useState(false);
  const [lastOrderWasPotential, setLastOrderWasPotential] = useState(false); // Order with send date after tomorrow → confirm on that date, lead stays in My Leads
  const [needsConfirmation, setNeedsConfirmation] = useState(false); // Operator marks order as "need confirmation" → show confirm-on date, shipping = next day after that
  const [confirmOnDate, setConfirmOnDate] = useState<string>(""); // YYYY-MM-DD when order must be confirmed; shipping date = next working day after this
  const [validatingBex, setValidatingBex] = useState(false);
  const [editingBexAddress, setEditingBexAddress] = useState(false);
  const [savingBexAddress, setSavingBexAddress] = useState(false);
  const [operatorActionsSortOrder, setOperatorActionsSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [orderNeedingConfirmation, setOrderNeedingConfirmation] = useState<any | null>(null); // Order with needsConfirmation for this lead (when lead has lastOrderNeedsConfirmation)
  const [pendingDeleteItemIndex, setPendingDeleteItemIndex] = useState<number | null>(null); // Index of order item pending delete confirmation
  const [showRejectOrderModal, setShowRejectOrderModal] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(false);
  const [showConfirmOrderModal, setShowConfirmOrderModal] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const { t } = useLanguage();

  // BEX Address data removed for Greek-CC (Greece uses ACS only)
  const municipalities: Municipality[] = [];
  const settlements: Settlement[] = [];
  const streets: Street[] = [];
  const bexDataLoaded = true;
  const bexDataLoading = false;
  
  // BEX Address selection state
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [selectedSettlementId, setSelectedSettlementId] = useState<number | null>(null);
  const [filteredSettlements, setFilteredSettlements] = useState<Settlement[]>([]);
  const [filteredStreets, setFilteredStreets] = useState<Street[]>([]);
  
  // Autocomplete states
  const [municipalitySearch, setMunicipalitySearch] = useState<string | null>(null);
  const [citySearch, setCitySearch] = useState<string | null>(null);
  const [streetSearch, setStreetSearch] = useState<string | null>(null);
  const [addressNumber, setAddressNumber] = useState<string>("");
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showStreetDropdown, setShowStreetDropdown] = useState(false);
  // Bypass browser autofill: start readOnly, become editable on first focus (Chrome ignores readonly for autofill)
  const [bexAutofillBypass, setBexAutofillBypass] = useState(false);

  // BEX address invalid when any displayed field is "—" (address wrong → cannot confirm or create order)
  const bexAddressInvalid = useMemo(() => {
    if (!leadData) return false;
    const area = leadData.area || "—";
    const city = leadData.city || "—";
    const zip = String(leadData.bexZipCode ?? leadData.index ?? "—");
    let street = "—";
    if (streetSearch) street = streetSearch;
    else if (leadData.bexMatchDetails?.street) street = leadData.bexMatchDetails.street;
    else if (leadData.address) {
      const parts = leadData.address.split(", ");
      const last = parts[parts.length - 1] || "";
      street = parseAddressComponents(last).street || last;
    }
    let addr = "—";
    if (addressNumber) addr = addressNumber;
    else if (leadData.addr) addr = leadData.addr;
    else if (leadData.address) {
      const parts = leadData.address.split(", ");
      const last = parts[parts.length - 1] || "";
      addr = parseAddressComponents(last).number || "—";
    }
    return area === "—" || city === "—" || zip === "—" || street === "—" || addr === "—";
  }, [leadData, streetSearch, addressNumber]);

  // Address is problematic when BEX has "—" or lead is flagged as problematic due to address → cannot create order
  const addressProblematic = bexAddressInvalid || leadData?.problematicReason === 'address';
  // Address is verified after edit when BEX matched or no "—" in any field → hide Problematic ribbon and Fix address banner
  const addressVerified = leadData?.bexMatched === true || !bexAddressInvalid;
  const showAddressProblematic = addressProblematic && !addressVerified;

  // Unique list of interested products (from interestedForProduct + externalHistory.matchedProduct) for new leads only
  const interestedProducts = useMemo(() => {
    if (!leadData || leadData.stage !== "new") return [];
    const seen = new Set<string>();
    const items: Array<{ productId: string; productName: string }> = [];
    const add = (p: { productId: string; productName: string } | null) => {
      if (!p?.productId || seen.has(String(p.productId))) return;
      seen.add(String(p.productId));
      items.push({ productId: p.productId, productName: p.productName || "" });
    };
    if (leadData.interestedForProduct) add(leadData.interestedForProduct);
    (leadData.externalHistory || []).forEach((h) => add(h.matchedProduct));
    return items;
  }, [leadData]);

  // When user selects "same city in other municipality", skip the municipality-effect auto-select so we don't overwrite
  const justSelectedOtherMunicipalityRef = useRef(false);

  // Snapshot of BEX address when Edit is opened; restored on Cancel
  const bexEditSnapshotRef = useRef<{
    municipalitySearch: string | null;
    citySearch: string | null;
    streetSearch: string | null;
    addressNumber: string;
    selectedMunicipalityId: number | null;
    selectedSettlementId: number | null;
  } | null>(null);
  
  // Profile Info state
  const [generalNotes, setGeneralNotes] = useState<string>("");
  const [healthInfo, setHealthInfo] = useState<Array<{ id: string; note: string; addedAt: string }>>([]);
  const [newHealthNote, setNewHealthNote] = useState<string>("");

  // Default "When should the order be sent?" to next working day when basket gets items
  useEffect(() => {
    if (orderItems.length > 0) {
      setRequestedSendDate((prev) => prev || getNextWorkingDate());
    }
  }, [orderItems.length]);

  // Pre-fill basket with order needing confirmation when lead has successful_with_confirmation
  useEffect(() => {
    if (!orderNeedingConfirmation || !leadData?.lastOrderNeedsConfirmation || products.length === 0) return;
    if (orderItems.length > 0) return; // Don't overwrite if user already has items

    const currency = (orderNeedingConfirmation.currency || "RSD").toUpperCase();
    let items: OrderItem[];

    // Prefer orderItems (full structure with includedProducts, isSpecialOffer, specialOfferId)
    const rawOrderItems = orderNeedingConfirmation.orderItems;
    if (rawOrderItems && Array.isArray(rawOrderItems) && rawOrderItems.length > 0) {
      items = rawOrderItems.map((item: any) => {
        const productId = String(item.productId ?? "");
        const productFromList = products.find((p: Product) => String(p.id) === productId);
        return {
          productId,
          productName: item.productName || "",
          quantity: item.quantity ?? 1,
          unitPrice: item.unitPrice ?? 0,
          totalPrice: item.totalPrice ?? (item.quantity ?? 1) * (item.unitPrice ?? 0),
          currency: item.currency || currency,
          image: productFromList?.image,
          discountNote: item.discountNote,
          isSpecialOffer: item.isSpecialOffer === true,
          specialOfferId: item.specialOfferId,
          includedProducts: item.includedProducts?.length
            ? item.includedProducts.map((ip: any) => ({
                productId: String(ip.productId ?? ""),
                productName: ip.productName || "",
                image: ip.image,
              }))
            : undefined,
          freeShipping: productFromList?.freeShipping,
        };
      });
    } else {
      // Fallback: build from goods (include includedProducts from productIds when present)
      const goods = orderNeedingConfirmation.goods || orderNeedingConfirmation.products || [];
      items = goods.map((g: any) => {
        const qty = g.count ?? g.quantity ?? 1;
        const unitPrice = g.price ?? 0;
        const productId = String(g.id ?? g.productId ?? "");
        const productFromList = products.find((p: Product) => String(p.id) === productId);
        const productIds = g.productIds;
        const includedProducts =
          productIds && Array.isArray(productIds) && productIds.length > 0
            ? productIds.map((pid: string) => {
                const p = products.find((pr: Product) => String(pr.id) === String(pid));
                return { productId: String(pid), productName: p?.name ?? "", image: p?.image };
              })
            : undefined;
        return {
          productId,
          productName: g.name || g.productName || "",
          quantity: qty,
          unitPrice,
          totalPrice: qty * unitPrice,
          currency,
          image: productFromList?.image,
          discountNote: g.discountNote,
          isSpecialOffer: Boolean(includedProducts?.length),
          includedProducts,
          freeShipping: productFromList?.freeShipping,
        };
      });
    }

    if (items.length > 0) {
      setOrderItems(items);
      setNeedsConfirmation(true);
      setConfirmOnDate(leadData.lastOrderConfirmOnDate || "");
    }
  }, [orderNeedingConfirmation, leadData?.lastOrderNeedsConfirmation, leadData?.lastOrderConfirmOnDate, products, orderItems.length]);

  // Fetch lead data
  useEffect(() => {
    const fetchLead = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          const lead = data.data.lead;
          setLeadData(lead);
          setOrderNeedingConfirmation(null);

          // Fetch all orders for this lead (multiple statuses: packing, in transit, delivered, canceled)
          try {
            const statusParams = [5, 6, 7, 8, 9, 10, 11].map((s) => `status=${s}`).join("&");
            const ordersResponse = await fetch(
              `${API_BASE_URL}/api/enhanced-orders/paginated?phone=${encodeURIComponent(data.data.lead.phone)}&${statusParams}&source=manual&leadId=${encodeURIComponent(leadId)}&limit=50&sortBy=time&sortOrder=desc`,
              {
                credentials: "include",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (ordersResponse.ok) {
              const ordersData = await ordersResponse.json();
              if (ordersData.success && ordersData.data?.orders && ordersData.data.orders.length > 0) {
                // Filter orders to only include those for this specific lead
                const leadOrders = ordersData.data.orders.filter((order: any) => {
                  return order.leadId === leadId || order.leadId === data.data.lead.id;
                });

                // When lead has lastOrderNeedsConfirmation, find the order that needs confirmation (most recent with needsConfirmation)
                if (lead?.lastOrderNeedsConfirmation && leadOrders.length > 0) {
                  const needingConfirmation = leadOrders.find((o: any) => o.needsConfirmation === true) ?? null;
                  setOrderNeedingConfirmation(needingConfirmation);
                }

                // Transform orders to match the expected format for display
                const transformedOrders = leadOrders.map((order: any) => {
                  // Parse the date
                  let orderDate: number | null = null;
                  if (order.time) {
                    if (typeof order.time === 'string') {
                      orderDate = new Date(order.time).getTime() / 1000;
                    } else if (typeof order.time === 'number') {
                      orderDate = order.time;
                    }
                  } else if (order.date) {
                    if (order.date.$date) {
                      orderDate = new Date(order.date.$date).getTime() / 1000;
                    } else if (typeof order.date === 'string') {
                      orderDate = new Date(order.date).getTime() / 1000;
                    } else if (typeof order.date === 'number') {
                      orderDate = order.date;
                    }
                  }

                  // Get currency from order or default to RSD
                  const currency = order.currency || "RSD";

                  // Extract products from order: goods, or orderItems (mapped so each has productId for image resolution), or products
                  const rawGoods = order.goods || order.products || [];
                  const fromOrderItems = order.orderItems?.length
                    ? order.orderItems.map((item: any) => ({
                        id: item.productId,
                        productId: item.productId,
                        name: item.productName || "",
                        productName: item.productName || "",
                        count: item.quantity ?? 1,
                        quantity: item.quantity ?? 1,
                        includedProducts: item.includedProducts,
                        productIds: item.includedProducts?.map((ip: any) => ip.productId).filter(Boolean) || [],
                      }))
                    : [];
                  const products = rawGoods.length > 0 ? rawGoods : fromOrderItems;

                  return {
                    id: order.id || order.orderId,
                    orderId: order.id || order.orderId,
                    time: orderDate,
                    offername: order.offername || (products.length > 0
                      ? products.map((p: any) => `${p.name || p.productName} x${p.quantity || 1}`).join(", ")
                      : "N/A"),
                    price: order.price || 0,
                    currency: currency,
                    status: order.status ?? 6,
                    cancelReason: order.cancelReason ?? order.reason,
                    cancelComment: order.comment || order.cancelComment,
                    products: products,
                    trackingId: order.trackingId ?? null,
                    needsConfirmation: order.needsConfirmation === true,
                    rejected: order.rejected === true,
                  };
                });

                setOrderHistory(transformedOrders);
              } else {
                setOrderHistory([]);
              }
            } else {
              setOrderHistory([]);
            }
          } catch (orderError) {
            console.error("Error fetching packing orders:", orderError);
            setOrderHistory([]);
          }
        } else {
          throw new Error(data.error || "Failed to load lead");
        }
      } catch (err) {
        console.error("Error fetching lead:", err);
        setError(err instanceof Error ? err.message : "Failed to load lead");
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [leadId]);

  // Load BEX address data
  // BEX address loading removed for Greek-CC (Greece uses ACS only)
  // useEffect(() => {
  //   if (!bexDataLoaded && !bexDataLoading) {
  //     dispatch(loadBexAddressData());
  //   }
  // }, [bexDataLoaded, bexDataLoading, dispatch]);

  // Initialize BEX selections when lead data loads
  useEffect(() => {
    if (leadData && municipalities.length > 0) {
      // Find municipality
      if (leadData.area) {
        const municipality = municipalities.find(m => 
          m.name.toLowerCase() === leadData.area!.toLowerCase()
        );
        if (municipality) {
          setSelectedMunicipalityId(municipality.id);
          setMunicipalitySearch(municipality.name);
        }
      }
      
      // Parse address to separate street and number
      // Priority: bexMatchDetails.street > parsed from address > leadData.address
      if (leadData.bexMatchDetails?.street) {
        // Use BEX matched street if available
        setStreetSearch(leadData.bexMatchDetails.street);
        // Try to get address number from addr, bexMatchDetails.addr, or parse from address if both are empty
        if (leadData.addr) {
          setAddressNumber(leadData.addr);
        } else if (leadData.bexMatchDetails?.addr) {
          // Use address number from bexMatchDetails if addr is missing
          setAddressNumber(leadData.bexMatchDetails.addr);
        } else if (leadData.address) {
          // Parse address to extract number
          const addressParts = leadData.address.split(", ");
          const lastPart = addressParts.length > 0 ? addressParts[addressParts.length - 1] : "";
          const { number } = parseAddressComponents(lastPart);
          setAddressNumber(number || "");
        } else {
          setAddressNumber("");
        }
      } else if (leadData.address) {
        // Parse the address string to extract street and number
        const addressParts = leadData.address.split(", ");
        const lastPart = addressParts.length > 0 ? addressParts[addressParts.length - 1] : "";
        const { street, number } = parseAddressComponents(lastPart);
        
        // If we found a number in the parsed address, use it
        if (number) {
          const streetName = street || (addressParts.length > 1 ? addressParts[addressParts.length - 2] : "") || "";
          setStreetSearch(streetName);
          setAddressNumber(number);
        } else {
          // No number found in address, use the last part as street (might contain number)
          // Try to extract number from the entire last part
          const { street: extractedStreet, number: extractedNumber } = parseAddressComponents(lastPart);
          if (extractedNumber) {
            setStreetSearch(extractedStreet);
            setAddressNumber(extractedNumber);
          } else {
            // Use last part as street, and addr from bexMatchDetails or leadData.addr if available
            setStreetSearch(lastPart);
            setAddressNumber(leadData.bexMatchDetails?.addr || leadData.addr || "");
          }
        }
      } else {
        // No address, just use addr from bexMatchDetails or leadData.addr if available
        setStreetSearch(null);
        setAddressNumber(leadData.bexMatchDetails?.addr || leadData.addr || "");
      }
    }
  }, [leadData, municipalities]);

  // Filter settlements when municipality changes
  useEffect(() => {
    if (selectedMunicipalityId) {
      const filtered = settlements.filter(s => s.municipalityId === selectedMunicipalityId);
      setFilteredSettlements(filtered);

      // Don't overwrite when user just selected "same city in other municipality"
      if (justSelectedOtherMunicipalityRef.current) {
        justSelectedOtherMunicipalityRef.current = false;
      } else if (leadData?.city && filtered.length > 0) {
        // Auto-select settlement if city matches
        const settlement = filtered.find(s =>
          s.name.toLowerCase() === leadData.city!.toLowerCase()
        );
        if (settlement) {
          setSelectedSettlementId(settlement.id);
          setCitySearch(settlement.name);
        }
      }
    } else {
      setFilteredSettlements(settlements);
    }
  }, [selectedMunicipalityId, settlements, leadData]);

  // Reset autofill bypass when closing BEX edit so the readonly trick works again on next open
  useEffect(() => {
    if (!editingBexAddress) setBexAutofillBypass(false);
  }, [editingBexAddress]);

  // Filter streets when settlement changes
  useEffect(() => {
    if (selectedSettlementId) {
      const filtered = streets.filter(s => s.settlementId === selectedSettlementId);
      setFilteredStreets(filtered);
      
      // Auto-select street if address matches
      if (leadData?.address && filtered.length > 0) {
        const street = filtered.find(s => 
          s.name.toLowerCase().includes(leadData.address!.toLowerCase()) ||
          leadData.address!.toLowerCase().includes(s.name.toLowerCase())
        );
        if (street) {
          setStreetSearch(street.name);
        }
      }
    } else {
      setFilteredStreets(streets);
    }
  }, [selectedSettlementId, streets, leadData]);

  // Initialize profile info from lead data (General Notes = notes, Health Info from healthInfo)
  useEffect(() => {
    if (leadData) {
      setGeneralNotes(leadData.notes || "");
      setHealthInfo(Array.isArray((leadData as any).healthInfo) ? (leadData as any).healthInfo : []);
    }
  }, [leadData]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.bex-address-container')) {
        setShowMunicipalityDropdown(false);
        setShowCityDropdown(false);
        setShowStreetDropdown(false);
      }
    };

    if (showMunicipalityDropdown || showCityDropdown || showStreetDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMunicipalityDropdown, showCityDropdown, showStreetDropdown]);

  // Fetch products: no caching – refetch when lead changes and when tab becomes visible so quantities are always current
  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/products?limit=100&_=${Date.now()}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.products) {
          const allProducts = data.data.products;
          const imagesMap: Record<string, string> = {};
          allProducts.forEach((p: any) => {
            const id = String(p._id || p.id);
            if (p.main_image) imagesMap[id] = p.main_image;
          });
          setProductImagesMap((prev) => ({ ...prev, ...imagesMap }));
          const inStockProducts = allProducts
            .filter((p: any) => {
              const productType = p.type || "single";
              if (productType === "bundle") {
                return (p.estimatedStock || 0) > 0;
              }
              return (p.quantity || 0) > 0;
            })
            .map((p: any) => ({
                id: p._id || p.id,
                name: p.product_name || p.name,
                price: p.price_rsd || p.price_mkd || p.price || 0,
                currency: p.currency || "RSD",
                image: p.main_image,
                sku: p.sku,
                quantity: p.quantity || 0,
                estimatedStock: p.estimatedStock || 0,
                type: p.type || "single",
                freeShipping: Boolean(p.freeShipping),
              }));
          setProducts(inStockProducts);
        }
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [leadId, fetchProducts]);

  // Fetch product images for interested items not in the main products list (e.g. out-of-stock)
  useEffect(() => {
    if (!leadData || leadData.stage !== "new" || interestedProducts.length === 0) return;
    const missing = interestedProducts.filter((item) => !productImagesMap[item.productId]);
    if (missing.length === 0) return;
    const fetchMissingImages = async () => {
      for (const item of missing) {
        try {
          const token = localStorage.getItem("authToken");
          const res = await fetch(`${API_BASE_URL}/api/products/${item.productId}`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          if (res.ok) {
            const data = await res.json();
            const img = data?.data?.product?.main_image;
            if (img) {
              setProductImagesMap((prev) => ({ ...prev, [item.productId]: img }));
            }
          }
        } catch {
          // ignore – we show placeholder
        }
      }
    };
    fetchMissingImages();
  }, [leadId, leadData, interestedProducts, productImagesMap]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchProducts();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchProducts]);

  // Fetch active offers
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_BASE_URL}/api/offers`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.offers) {
            const now = new Date();
            // Filter to only active offers that are currently valid
            const activeOffers = data.data.offers.filter((offer: Offer) => {
              if (!offer.isActive) return false;
              if (offer.unlimited) return true;
              if (!offer.startDate || !offer.endDate) return false;
              const start = new Date(offer.startDate);
              const end = new Date(offer.endDate);
              return now >= start && now <= end;
            });
            setOffers(activeOffers);
          }
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      }
    };

    fetchOffers();
  }, []);

  const handleFieldChange = (field: keyof LeadData, value: any) => {
    if (!leadData) return;
    setLeadData({
      ...leadData,
      [field]: value,
    });
  };

  const handleAddProduct = (product: Product) => {
    // Check stock availability based on product type
    const availableStock = product.type === "bundle" 
      ? (product.estimatedStock || 0)
      : (product.quantity || 0);
    
    if (availableStock <= 0) {
      setError(`Product "${product.name}" is out of stock`);
      return;
    }

    const existingItem = orderItems.find((item) => item.productId === product.id);
    if (existingItem) {
      // Check if adding one more would exceed stock
      if (existingItem.quantity + 1 > availableStock) {
        setError(`Cannot add more. Only ${availableStock} available in stock for "${product.name}"`);
        return;
      }
      setOrderItems(
        orderItems.map((item) =>
          item.productId === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                totalPrice: (item.quantity + 1) * item.unitPrice,
              }
            : item
        )
      );
    } else {
      setOrderItems([
        ...orderItems,
        {
          productId: product.id,
          productName: product.name,
          quantity: 1,
          unitPrice: product.price,
          totalPrice: product.price,
          currency: product.currency,
          image: product.image,
        },
      ]);
    }
  };

  const handleSelectOffer = (offer: Offer) => {
    const offerId = offer.id || offer._id || '';
    
    // For special offers, toggle the active selection state
    if (offer.type === 'special') {
      // Check if this offer already has max products selected in the basket
      const existingSelection = selectedOffers.get(offerId);
      const hasMaxProducts = existingSelection && offer.maxPickCount && existingSelection.products.length === offer.maxPickCount;
      
      // If clicking the same offer that's currently active, just toggle it
      if (selectedOffer && (selectedOffer.id === offer.id || selectedOffer._id === offer._id)) {
        // If it has selected products, save them first
        if (selectedOfferProducts.length > 0) {
          const newSelectedOffers = new Map(selectedOffers);
          newSelectedOffers.set(offerId, { offer, products: selectedOfferProducts });
          setSelectedOffers(newSelectedOffers);
          updateOrderItemsFromSelectedOffers(newSelectedOffers);
        } else {
          // If no products selected, remove from basket
          const newSelectedOffers = new Map(selectedOffers);
          newSelectedOffers.delete(offerId);
          setSelectedOffers(newSelectedOffers);
          updateOrderItemsFromSelectedOffers(newSelectedOffers);
        }
        // Close the selection UI
        setSelectedOffer(null);
        setSelectedOfferProducts([]);
        return;
      }
      
      // If this offer already has max products selected, don't reopen selection - just keep it in basket
      if (hasMaxProducts) {
        // Offer is already complete, don't reopen selection
        return;
      }
      
      // Save current offer's selected products before switching (if any)
      if (selectedOffer && selectedOffer.type === 'special' && selectedOfferProducts.length > 0) {
        const currentOfferId = selectedOffer.id || selectedOffer._id || '';
        const newSelectedOffers = new Map(selectedOffers);
        newSelectedOffers.set(currentOfferId, { offer: selectedOffer, products: selectedOfferProducts });
        setSelectedOffers(newSelectedOffers);
        // Don't update order items here - wait until we switch
      }
      
      // Set as active offer for product selection
      setSelectedOffer(offer);
      // Load previously selected products for this offer if any (independent from other offers)
      const previouslySelected = selectedOffers.get(offerId);
      setSelectedOfferProducts(previouslySelected?.products || []);
      return;
    }
    
    // For gift offers, toggle in/out of basket
    if (offer.type === 'gift') {
      const isInBasket = selectedOffers.has(offerId);
      
      if (isInBasket) {
        // Remove from basket
        const newSelectedOffers = new Map(selectedOffers);
        newSelectedOffers.delete(offerId);
        setSelectedOffers(newSelectedOffers);
        updateOrderItemsFromSelectedOffers(newSelectedOffers);
      } else {
        // Add to basket
        const newSelectedOffers = new Map(selectedOffers);
        newSelectedOffers.set(offerId, { offer, products: [] });
        setSelectedOffers(newSelectedOffers);
        updateOrderItemsFromSelectedOffers(newSelectedOffers);
      }
      return;
    }
  };

  // Helper function to update order items from all selected offers
  const updateOrderItemsFromSelectedOffers = (offersMap: Map<string, { offer: Offer; products: string[] }>) => {
    // Get all non-special-offer items (regular products only)
    const regularItems = orderItems.filter(item => !item.isSpecialOffer);
    
    // Create order items for all offers (special and gift)
    const offerItems: OrderItem[] = [];
    
    offersMap.forEach(({ offer, products: selectedProducts }) => {
      if (offer.type === 'special') {
        // For special offers, only add if products are selected
        if (selectedProducts.length === 0) return;
        
        // For special offers, create a single order item with quantity 1 and special price
        const includedProducts = selectedProducts.map(pid => {
          const product = products.find(p => p.id === pid);
          return product ? {
            productId: product.id,
            productName: product.name,
            image: product.image,
          } : null;
        }).filter(Boolean) as Array<{ productId: string; productName: string; image?: string }>;
        
        // Get first product for image/currency
        const firstProduct = products.find(p => p.id === selectedProducts[0]);
        
        // Create single order item for special offer
        const specialOfferItem: OrderItem = {
          productId: offer.id || offer._id || 'special-offer',
          productName: `${offer.name} (${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''})`,
          quantity: 1, // Single quantity for special offer
          unitPrice: offer.specialPrice || 0, // Special price as unit price
          totalPrice: offer.specialPrice || 0, // Total price is the special price
          currency: firstProduct?.currency || "RSD",
          image: firstProduct?.image,
          isSpecialOffer: true,
          specialOfferId: offer.id || offer._id,
          includedProducts: includedProducts,
          freeShipping: offer.freeShipping === true,
        };
        
        offerItems.push(specialOfferItem);
      } else if (offer.type === 'gift') {
        // For gift offers, create a single order item with quantity 1 and combined price
        const baseProductsList: Array<{ productId: string; productName: string; image?: string }> = [];
        const giftItemsList: Array<{ productId: string; productName: string; image?: string }> = [];
        let totalBasePrice = 0;
        let firstProduct = null;
        
        // Collect base products
        if (offer.baseProducts && offer.baseProducts.length > 0) {
          for (const baseProduct of offer.baseProducts) {
            const product = products.find(p => 
              p.id === baseProduct.productId || 
              p.id === String(baseProduct.productId) ||
              String(p.id) === String(baseProduct.productId)
            );
            
            if (product) {
              if (!firstProduct) firstProduct = product;
              totalBasePrice += product.price;
              baseProductsList.push({
                productId: product.id,
                productName: product.name,
                image: product.image,
              });
            } else {
              baseProductsList.push({
                productId: baseProduct.productId,
                productName: baseProduct.productName,
              });
            }
          }
        }
        
        // Collect gift items
        if (offer.giftItems && offer.giftItems.length > 0) {
          for (const giftItem of offer.giftItems) {
            const product = products.find(p => 
              p.id === giftItem.productId || 
              p.id === String(giftItem.productId) ||
              String(p.id) === String(giftItem.productId)
            );
            
            if (product) {
              if (!firstProduct) firstProduct = product;
              giftItemsList.push({
                productId: product.id,
                productName: product.name,
                image: product.image,
              });
            } else {
              giftItemsList.push({
                productId: giftItem.productId,
                productName: giftItem.productName,
              });
            }
          }
        }
        
        // Create single order item for gift offer
        const giftOfferItem: OrderItem = {
          productId: offer.id || offer._id || 'gift-offer',
          productName: offer.name,
          quantity: 1, // Single quantity for gift offer
          unitPrice: totalBasePrice, // Total price of base products
          totalPrice: totalBasePrice, // Total price is the base products price
          currency: firstProduct?.currency || "RSD",
          image: firstProduct?.image,
          isSpecialOffer: true, // Treat gift offers like special offers
          specialOfferId: offer.id || offer._id,
          includedProducts: [
            ...baseProductsList.map(bp => ({ productId: bp.productId, productName: bp.productName, image: bp.image })),
            ...giftItemsList.map(gi => ({ productId: gi.productId, productName: `${gi.productName} (Gift)`, image: gi.image })),
          ],
          discountNote: giftItemsList.length > 0 ? "Gift" : undefined, // Auto-fill "Gift" if there are gift items
          freeShipping: offer.freeShipping === true,
        };
        
        offerItems.push(giftOfferItem);
      }
    });
    
    // Combine regular items with offer items
    setOrderItems([...regularItems, ...offerItems]);
  };

  const handleSelectOfferProduct = (productId: string) => {
    if (!selectedOffer || selectedOffer.type !== 'special') return;
    
    const offer = selectedOffer;
    const offerId = offer.id || offer._id || '';
    if (!offer.maxPickCount || !offer.specialPrice) return;
    
    let newSelectedProducts: string[];
    if (selectedOfferProducts.includes(productId)) {
      // Deselect
      newSelectedProducts = selectedOfferProducts.filter(id => id !== productId);
    } else {
      // Check if we've reached max pick count
      if (selectedOfferProducts.length >= offer.maxPickCount) {
        setError(`You can only select ${offer.maxPickCount} product(s) for this special offer`);
        return;
      }
      newSelectedProducts = [...selectedOfferProducts, productId];
    }
    
    setSelectedOfferProducts(newSelectedProducts);
    
    // Update the selected offers map
    const newSelectedOffers = new Map(selectedOffers);
    if (newSelectedProducts.length === 0) {
      // Remove offer if no products selected
      newSelectedOffers.delete(offerId);
    } else {
      // Add/update offer with selected products (mark as in basket)
      newSelectedOffers.set(offerId, { offer, products: newSelectedProducts });
    }
    setSelectedOffers(newSelectedOffers);
    
    // Update order items from all selected offers
    updateOrderItemsFromSelectedOffers(newSelectedOffers);
    
    // If we've selected the required number of products, save and close selection UI
    if (newSelectedProducts.length === offer.maxPickCount) {
      // Close the selection UI after max products selected (selection is already saved to selectedOffers)
      setSelectedOffer(null);
      setSelectedOfferProducts([]);
    }
  };

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) {
      setOrderItems(orderItems.filter((item) => item.productId !== productId));
      // If removing from special offer selection, also remove from selectedOfferProducts
      if (selectedOffer && selectedOffer.type === 'special') {
        setSelectedOfferProducts(selectedOfferProducts.filter(id => id !== productId));
      }
      return;
    }
    
    const item = orderItems.find((item) => item.productId === productId);
    
    // For special offers, update quantity and recalculate total price
    if (item?.isSpecialOffer) {
      setOrderItems(
        orderItems.map((item) =>
          item.productId === productId
            ? {
                ...item,
                quantity,
                totalPrice: quantity * item.unitPrice, // quantity * specialPrice
              }
            : item
        )
      );
      return;
    }
    
    // For regular products, check stock availability
    const product = products.find(p => p.id === productId);
    if (product) {
      const availableStock = product.type === "bundle"
        ? (product.estimatedStock || 0)
        : (product.quantity || 0);
      
      if (quantity > availableStock) {
        setError(`Cannot add more. Only ${availableStock} available in stock for "${product.name}"`);
        return;
      }
    }
    
    setOrderItems(
      orderItems.map((item) =>
        item.productId === productId
          ? {
              ...item,
              quantity,
              totalPrice: quantity * item.unitPrice,
            }
          : item
      )
    );
  };

  const handleUpdatePrice = (productId: string, newPrice: number, originalPrice: number) => {
    const discountPercent = ((originalPrice - newPrice) / originalPrice) * 100;
    const needsNote = discountPercent >= 70;

    setOrderItems(
      orderItems.map((item) =>
        item.productId === productId
          ? {
              ...item,
              unitPrice: newPrice,
              totalPrice: item.quantity * newPrice,
              discountNote: needsNote ? item.discountNote || "" : undefined,
            }
          : item
      )
    );
  };

  const handleSaveBexAddress = async () => {
    if (!leadData) return;
    
    setSavingBexAddress(true);
    setError(null);
    
    try {
      const token = localStorage.getItem("authToken");
      const area = municipalitySearch ?? leadData.area ?? "";
      const city = citySearch ?? leadData.city ?? "";
      const street = streetSearch ?? leadData.bexMatchDetails?.street ?? leadData.address ?? "";
      const addr = addressNumber || leadData.addr || "";
      
      const validateRes = await fetch(`${API_BASE_URL}/api/shipping/bex/validate-address`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ area, city, street, addr }),
      });
      const validateData = await validateRes.json().catch(() => ({}));
      if (!validateRes.ok || !validateData.success) {
        setError(validateData.error || "Address must be verified by BEX before saving.");
        setSavingBexAddress(false);
        return;
      }
      
      // Unified address: store street-only in address; area= municipality, city= settlement, addr= number.
      const streetOnly = streetSearch || leadData.bexMatchDetails?.street || leadData.address || null;
      const zipCode = selectedSettlementId
        ? (settlements.find(s => s.id === selectedSettlementId)?.zip || leadData.index)
        : leadData.index;

      let updatedBexMatchDetails = leadData.bexMatchDetails || null;
      if (selectedMunicipalityId && selectedSettlementId && streetSearch) {
        const selectedMunicipality = municipalities.find(m => m.id === selectedMunicipalityId);
        const selectedSettlement = settlements.find(s => s.id === selectedSettlementId);
        updatedBexMatchDetails = {
          municipality: selectedMunicipality?.name || leadData.bexMatchDetails?.municipality || null,
          settlement: selectedSettlement?.name || leadData.bexMatchDetails?.settlement || null,
          street: streetSearch || leadData.bexMatchDetails?.street || null,
          addr: addressNumber || leadData.addr || leadData.bexMatchDetails?.addr || null,
          confidence: 'high'
        };
      } else if (leadData.bexMatchDetails && addressNumber) {
        updatedBexMatchDetails = { ...leadData.bexMatchDetails, addr: addressNumber };
      }

      const updateData = {
        ...leadData,
        area: municipalitySearch || leadData.area,
        city: citySearch || leadData.city,
        address: streetOnly,
        addr: addressNumber || leadData.addr || null,
        index: zipCode,
        bexZipCode: zipCode,
        bexMatched: !!(selectedMunicipalityId && selectedSettlementId && streetSearch),
        bexMatchDetails: updatedBexMatchDetails,
      };
      
      const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save address");
      }
      
      setLeadData(data.data.lead || updateData);
      setEditingBexAddress(false);
    } catch (err) {
      console.error("Error saving BEX address:", err);
      setError(err instanceof Error ? err.message : "Failed to save address");
    } finally {
      setSavingBexAddress(false);
    }
  };

  const handleAddHealthNote = () => {
    if (!newHealthNote.trim()) return;
    const newNote = {
      id: Date.now().toString(),
      note: newHealthNote,
      addedAt: new Date().toISOString(),
    };
    setHealthInfo([...healthInfo, newNote]);
    setNewHealthNote("");
  };

  const handleRemoveHealthNote = (id: string) => {
    setHealthInfo(healthInfo.filter(note => note.id !== id));
  };

  /** Create order from current basket (used when user clicks End Call & Save with outcome Successful and items in basket). */
  const createOrderFromBasket = useCallback(async (): Promise<void> => {
    if (orderItems.length === 0 || !leadData) return;
    const token = localStorage.getItem("authToken");
    const totalPrice = orderItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const firstItem = orderItems[0];
    let offerName: string;
    if (selectedOffer && selectedOffer.type === "special") {
      const specialOfferItem = orderItems.find((item) => item.isSpecialOffer);
      if (specialOfferItem?.includedProducts?.length) {
        offerName = `${selectedOffer.name} (${specialOfferItem.includedProducts.map((p) => p.productName).join(", ")})`;
      } else {
        offerName = selectedOffer.name;
      }
    } else {
      offerName = orderItems.map((i) => `${i.productName} x${i.quantity}`).join(", ");
      if (selectedOffer?.type === "gift") offerName = `${selectedOffer.name}: ${offerName}`;
    }
    const freeShipping =
      orderItems.length > 0 &&
      orderItems.every((item) => {
        if (item.isSpecialOffer) {
          if (item.freeShipping === true) return true;
          if (item.includedProducts?.length) {
            return item.includedProducts.every((ip) => Boolean(products.find((pr) => String(pr.id) === String(ip.productId))?.freeShipping));
          }
          return false;
        }
        return Boolean(products.find((pr) => String(pr.id) === String(item.productId))?.freeShipping);
      });
    const shippingDateYmd =
      needsConfirmation && confirmOnDate
        ? getNextWorkingDateAfter(confirmOnDate)
        : requestedSendDate || getNextWorkingDate();
    const orderNeedsConfirmation = Boolean(needsConfirmation && confirmOnDate);
    const orderData = {
      name: leadData.name,
      phone: leadData.phone,
      email: leadData.email || "",
      gender: 0,
      country: "rs",
      city: leadData.city || "",
      area: leadData.area || "N/A",
      street: streetSearch || leadData.address || "",
      addr: addressNumber || leadData.addr || "",
      index: leadData.bexZipCode || leadData.index || "",
      offer: parseInt(firstItem.productId as string, 10) || null,
      offername: offerName,
      count: orderItems.reduce((sum, item) => sum + item.quantity, 0),
      price: totalPrice,
      currency: firstItem.currency.toLowerCase(),
      user: null,
      wm: null,
      status: 6,
      stage: 0,
      source: "manual",
      delivery: 1,
      discount: 0,
      base: 0,
      paid: 0,
      done: 0,
      phase: 3,
      reason: 0,
      hold: 0,
      check: 0,
      time: Math.floor(Date.now() / 1000),
      leadId: leadId,
      comment: shippingComment || undefined,
      requestedSendDate: shippingDateYmd,
      isPotentialOrder: orderNeedsConfirmation ? false : isPotentialOrderDate(shippingDateYmd),
      needsConfirmation: orderNeedsConfirmation || undefined,
      confirmOnDate: orderNeedsConfirmation ? confirmOnDate : isPotentialOrderDate(shippingDateYmd) ? shippingDateYmd : undefined,
      offerId: orderItems.find((item) => item.isSpecialOffer)?.specialOfferId || selectedOffer?.id || selectedOffer?._id || null,
      offerType: orderItems.find((item) => item.isSpecialOffer) ? "special" : selectedOffer?.type || null,
      freeShipping: freeShipping || undefined,
      orderItems: orderItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
        currency: item.currency,
        discountNote: item.discountNote,
        isSpecialOffer: item.isSpecialOffer,
        specialOfferId: item.specialOfferId,
        includedProducts: item.includedProducts,
      })),
    };
    const response = await fetch(`${API_BASE_URL}/api/orders/create`, {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create order");
    }
    const data = await response.json();
    if (!data.success) throw new Error(data.error || "Failed to create order");
    setLastOrderWasPotential(orderNeedsConfirmation || isPotentialOrderDate(shippingDateYmd));
    setOrderItems([]);
    setSelectedOffer(null);
    setSelectedOfferProducts([]);
    setSelectedOffers(new Map());
    if (leadData && token) {
      try {
        const statusParams = [5, 6, 7, 8, 9, 10, 11].map((s) => `status=${s}`).join("&");
        const ordersResponse = await fetch(
          `${API_BASE_URL}/api/enhanced-orders/paginated?phone=${encodeURIComponent(leadData.phone)}&${statusParams}&source=manual&leadId=${encodeURIComponent(leadId)}&limit=50&sortBy=time&sortOrder=desc`,
          { credentials: "include", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (ordersResponse.ok) {
          const ordersData = await ordersResponse.json();
          if (ordersData.success && ordersData.data?.orders?.length) {
            const leadOrders = ordersData.data.orders.filter((o: any) => o.leadId === leadId || o.leadId === (leadData as any)?.id);
            const transformedOrders = leadOrders.map((order: any) => {
              let orderDate: number | null = null;
              if (order.time) orderDate = typeof order.time === "string" ? new Date(order.time).getTime() / 1000 : order.time;
              const rawGoods = order.goods || order.products || [];
              const fromOrderItems = order.orderItems?.length
                ? order.orderItems.map((item: any) => ({
                    id: item.productId,
                    productId: item.productId,
                    name: item.productName || "",
                    productName: item.productName || "",
                    count: item.quantity ?? 1,
                    quantity: item.quantity ?? 1,
                    includedProducts: item.includedProducts,
                    productIds: item.includedProducts?.map((ip: any) => ip.productId).filter(Boolean) || [],
                  }))
                : [];
              const products = rawGoods.length > 0 ? rawGoods : fromOrderItems;
              return {
                id: order.id || order.orderId,
                orderId: order.id || order.orderId,
                time: orderDate,
                offername: order.offername || (products.length ? products.map((p: any) => `${p.name || p.productName} x${p.quantity || 1}`).join(", ") : "N/A"),
                price: order.price || 0,
                currency: order.currency || "RSD",
                status: order.status ?? 6,
                cancelReason: order.cancelReason ?? order.reason,
                cancelComment: order.comment || order.cancelComment,
                products,
                trackingId: order.trackingId ?? null,
                needsConfirmation: order.needsConfirmation === true,
                rejected: order.rejected === true,
              };
            });
            setOrderHistory(transformedOrders);
          }
        }
      } catch (e) {
        console.error("Error refreshing order history:", e);
      }
    }
  }, [leadData, leadId, orderItems, products, requestedSendDate, shippingComment, streetSearch, addressNumber, selectedOffer, needsConfirmation, confirmOnDate]);

  const handleEndCall = () => {
    setEndCallModalFromCreateOrder(false);
    if (orderItems.length > 0) {
      setCallNotes((c) => ({
        ...c,
        callOutcome: needsConfirmation && confirmOnDate ? "successful_with_confirmation" : "successful",
      }));
    }
    setShowEndCallModal(true);
  };

  /** Full save and end call. When openNextLead is true, opens next lead (Save & Next Call); when false, stays on my-leads (End Only). */
  const handleSaveAndEndCall = async (openNextLead: boolean = true) => {
    if (!leadData) return;

    if (!callNotes.callOutcome) {
      setError("Call Outcome is required to end the call");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Create order only when user clicks End Call & Save with outcome Successful and has items in basket
      let orderHadNeedsConfirmation = false;
      let orderConfirmOnDate: string | null = null;
      const isSuccessfulOutcome = callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation";
      if (isSuccessfulOutcome && orderItems.length > 0) {
        await createOrderFromBasket();
        orderHadNeedsConfirmation = needsConfirmation && !!confirmOnDate;
        orderConfirmOnDate = orderHadNeedsConfirmation ? confirmOnDate : null;
      }

      const token = localStorage.getItem("authToken");
      const staysInMyLeads =
        callNotes.callOutcome === "callback" ||
        callNotes.callOutcome === "no_answer" ||
        callNotes.callOutcome === "busy_line" ||
        callNotes.callOutcome === "unreachable" ||
        lastOrderWasPotential ||
        (isSuccessfulOutcome && orderItems.length > 0 && orderHadNeedsConfirmation);

      const updateData: Record<string, unknown> = {
        ...leadData,
        phone: normalizePhoneToStartWithZero(leadData.phone),
        notes: generalNotes, // General Notes (saved as notes in DB)
        healthInfo: healthInfo, // Health Info (saved in DB)
        ...(callNotes.notes && { callNotes: callNotes.notes }),
        ...(callNotes.callRating > 0 && { callRating: callNotes.callRating }),
        ...(callNotes.callOutcome && { callOutcome: callNotes.callOutcome }),
        ...(callNotes.callOutcome === "not_interested" && callNotes.notInterestedReason && { notInterestedReason: callNotes.notInterestedReason }),
        ...(callNotes.followUpDate && { followUpDate: callNotes.followUpDate }),
        ...(callNotes.followUpHours && { followUpHours: callNotes.followUpHours, bestTimeToCall: callNotes.followUpHours }),
        ...(callNotes.followUpNotes && { followUpNotes: callNotes.followUpNotes }),
        ...(callNotes.followUpPotentialProduct && { followUpPotentialProduct: callNotes.followUpPotentialProduct }),
        lastContactDate: new Date().toISOString(),
        leadStatus: (callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation") ? "contacted" : leadData.leadStatus || "new",
        // Mark call as completed (timestamp + operator)
        callCenterCompletedAt: new Date().toISOString(),
        callCenterCompletedBy: {
          userId: currentUser.id,
          username: currentUser.username,
          email: currentUser.email,
          role: currentUser.role,
        },
      };
      if (isSuccessfulOutcome && orderItems.length > 0) {
        updateData.lastOrderNeedsConfirmation = orderHadNeedsConfirmation;
        updateData.lastOrderConfirmOnDate = orderHadNeedsConfirmation ? orderConfirmOnDate : null;
      }
      // Clear "Need confirmation" status when next call ends as confirmed (successful) or not interested (with reason)
      if (callNotes.callOutcome === "not_interested") {
        updateData.lastOrderNeedsConfirmation = false;
        updateData.lastOrderConfirmOnDate = null;
      }
      if (callNotes.callOutcome === "successful") {
        updateData.lastOrderNeedsConfirmation = false;
        updateData.lastOrderConfirmOnDate = null;
      }
      if (!staysInMyLeads) {
        updateData.sentToCallCenter = false;
        updateData.leadOwner = null;
      }

      const response = await fetch(`${API_BASE_URL}/api/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to save changes");
      }

      // Close the End Call modal
      setShowEndCallModal(false);

      // Release call lock
      if (onCallEnd) {
        onCallEnd();
      }

      onSave();

      // Open next lead (Save & Next Call) or stay on my-leads (End Only)
      if (openNextLead && onTakeNextLead) {
        try {
          await onTakeNextLead(leadId);
        } catch (err) {
          console.error("Error taking next lead:", err);
          onBack();
        }
      } else {
        onBack();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  /** End Only: same full save as Save & Next Call, but stay on my-leads (do not open next lead). */
  const handleEndCallAndExit = () => {
    handleSaveAndEndCall(false);
  };

  const handleRejectOrderConfirm = useCallback(async () => {
    if (!orderNeedingConfirmation?.id || !leadData?.phone) return;
    setRejectingOrder(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/api/orders/${orderNeedingConfirmation.id}/reject`, {
        method: "POST",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to reject order");
      }
      setLeadData((prev) =>
        prev ? { ...prev, lastOrderNeedsConfirmation: false, lastOrderConfirmOnDate: undefined } : null
      );
      setOrderNeedingConfirmation(null);
      setShowRejectOrderModal(false);
      setOrderItems([]);
      setSelectedOffer(null);
      setSelectedOfferProducts([]);
      setSelectedOffers(new Map());
      const statusParams = [5, 6, 7, 8, 9, 10, 11].map((s) => `status=${s}`).join("&");
      const ordersRes = await fetch(
        `${API_BASE_URL}/api/enhanced-orders/paginated?phone=${encodeURIComponent(leadData.phone)}&${statusParams}&source=manual&leadId=${encodeURIComponent(leadId)}&limit=50&sortBy=time&sortOrder=desc`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        if (ordersData.success && ordersData.data?.orders?.length) {
          const leadOrders = ordersData.data.orders.filter(
            (o: any) => o.leadId === leadId || o.leadId === (leadData as any)?.id
          );
          const transformedOrders = leadOrders.map((order: any) => {
            let orderDate: number | null = null;
            if (order.time)
              orderDate = typeof order.time === "string" ? new Date(order.time).getTime() / 1000 : order.time;
            const rawGoods = order.goods || order.products || [];
            const fromOrderItems = order.orderItems?.length
              ? order.orderItems.map((item: any) => ({
                  id: item.productId,
                  productId: item.productId,
                  name: item.productName || "",
                  productName: item.productName || "",
                  count: item.quantity ?? 1,
                  quantity: item.quantity ?? 1,
                  includedProducts: item.includedProducts,
                  productIds: item.includedProducts?.map((ip: any) => ip.productId).filter(Boolean) || [],
                }))
              : [];
            const products = rawGoods.length > 0 ? rawGoods : fromOrderItems;
            return {
              id: order.id || order.orderId,
              orderId: order.id || order.orderId,
              time: orderDate,
              offername:
                order.offername ||
                (products.length
                  ? products.map((p: any) => `${p.name || p.productName} x${p.quantity || 1}`).join(", ")
                  : "N/A"),
              price: order.price || 0,
              currency: order.currency || "RSD",
              status: order.status ?? 6,
              cancelReason: order.cancelReason ?? order.reason,
              cancelComment: order.comment || order.cancelComment,
              products,
              trackingId: order.trackingId ?? null,
              needsConfirmation: order.needsConfirmation === true,
              rejected: order.rejected === true,
            };
          });
          setOrderHistory(transformedOrders);
        } else {
          setOrderHistory([]);
        }
      }

      // Give user new lead (same as end-call): release call lock, save, then open next lead or go back
      if (onCallEnd) onCallEnd();
      onSave();
      if (onTakeNextLead) {
        try {
          await onTakeNextLead(leadId);
        } catch (err) {
          console.error("Error taking next lead after reject:", err);
          onBack();
        }
      } else {
        onBack();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject order");
    } finally {
      setRejectingOrder(false);
    }
  }, [orderNeedingConfirmation, leadData, leadId, onCallEnd, onSave, onTakeNextLead, onBack]);

  const handleConfirmOrderConfirm = useCallback(async () => {
    if (!orderNeedingConfirmation?.id || !leadData?.phone) return;
    setConfirmingOrder(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/api/orders/${orderNeedingConfirmation.id}/confirm`, {
        method: "POST",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to confirm order");
      }
      setLeadData((prev) => {
        if (!prev) return null;
        const next = { ...prev, lastOrderNeedsConfirmation: false, lastOrderConfirmOnDate: undefined, callOutcome: "successful" as const };
        const actions = prev.operatorActions;
        if (Array.isArray(actions) && actions.length > 0) {
          for (let i = actions.length - 1; i >= 0; i--) {
            const a = actions[i];
            if (a && (a as any).action === "end_call" && (a as any).callOutcome === "successful_with_confirmation") {
              next.operatorActions = [...actions];
              (next.operatorActions as any[])[i] = { ...a, callOutcome: "successful" };
              break;
            }
          }
        }
        return next;
      });
      setOrderNeedingConfirmation(null);
      setShowConfirmOrderModal(false);
      setOrderItems([]);
      setSelectedOffer(null);
      setSelectedOfferProducts([]);
      setSelectedOffers(new Map());
      const statusParams = [5, 6, 7, 8, 9, 10, 11].map((s) => `status=${s}`).join("&");
      const ordersRes = await fetch(
        `${API_BASE_URL}/api/enhanced-orders/paginated?phone=${encodeURIComponent(leadData.phone)}&${statusParams}&source=manual&leadId=${encodeURIComponent(leadId)}&limit=50&sortBy=time&sortOrder=desc`,
        { credentials: "include", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        if (ordersData.success && ordersData.data?.orders?.length) {
          const leadOrders = ordersData.data.orders.filter(
            (o: any) => o.leadId === leadId || o.leadId === (leadData as any)?.id
          );
          const transformedOrders = leadOrders.map((order: any) => {
            let orderDate: number | null = null;
            if (order.time)
              orderDate = typeof order.time === "string" ? new Date(order.time).getTime() / 1000 : order.time;
            const rawGoods = order.goods || order.products || [];
            const fromOrderItems = order.orderItems?.length
              ? order.orderItems.map((item: any) => ({
                  id: item.productId,
                  productId: item.productId,
                  name: item.productName || "",
                  productName: item.productName || "",
                  count: item.quantity ?? 1,
                  quantity: item.quantity ?? 1,
                  includedProducts: item.includedProducts,
                  productIds: item.includedProducts?.map((ip: any) => ip.productId).filter(Boolean) || [],
                }))
              : [];
            const products = rawGoods.length > 0 ? rawGoods : fromOrderItems;
            return {
              id: order.id || order.orderId,
              orderId: order.id || order.orderId,
              time: orderDate,
              offername:
                order.offername ||
                (products.length
                  ? products.map((p: any) => `${p.name || p.productName} x${p.quantity || 1}`).join(", ")
                  : "N/A"),
              price: order.price || 0,
              currency: order.currency || "RSD",
              status: order.status ?? 6,
              cancelReason: order.cancelReason ?? order.reason,
              cancelComment: order.comment || order.cancelComment,
              products,
              trackingId: order.trackingId ?? null,
              needsConfirmation: order.needsConfirmation === true,
              rejected: order.rejected === true,
            };
          });
          setOrderHistory(transformedOrders);
        } else {
          setOrderHistory([]);
        }
      }
      onSave?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm order");
    } finally {
      setConfirmingOrder(false);
    }
  }, [orderNeedingConfirmation, leadData, leadId, onSave]);

  const handleBack = async () => {
    // When lead has order needing confirmation, do not record skip – keep lead assigned to operator so it stays in My Leads
    if (!leadData?.lastOrderNeedsConfirmation) {
      try {
        const token = localStorage.getItem("authToken");
        await fetch(`${API_BASE_URL}/api/leads/${leadId}/record-skip`, {
          method: "POST",
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) {
        // Ignore; still navigate back
      }
    }
    onBack();
  };

  const handleAddRedFlag = () => {
    if (!leadData) return;
    const flagType = prompt(t('common.flagType'));
    const reason = prompt(t('common.reason'));
    if (flagType && reason) {
      const newFlag = {
        type: flagType,
        reason: reason,
        addedAt: new Date().toISOString(),
        addedBy: currentUser.username,
      };
      setLeadData({
        ...leadData,
        redFlags: [...(leadData.redFlags || []), newFlag],
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[#264a3e] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loadingLeadDetails')}</p>
        </div>
      </div>
    );
  }

  if (error && !leadData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('common.errorLoadingLead')}</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1a3329]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  if (!leadData) return null;

  const totalOrderValue = orderItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const hasRedFlags = leadData.redFlags && leadData.redFlags.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('common.back')}
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{leadData.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  {leadData.email && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="text-sm text-gray-500">{leadData.email}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleEndCall}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1a3329]"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('leads.endCall')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-800 flex-1">{error}</p>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Lead Information Cards (Scrollable) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Red Flags Card */}
            {hasRedFlags && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Flag className="w-5 h-5 text-red-600 mr-2" />
                    <h3 className="text-lg font-semibold text-red-900">{t('leads.redFlags')}</h3>
                  </div>
                  <button
                    onClick={handleAddRedFlag}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    + {t('leads.addFlag')}
                  </button>
                </div>
                <div className="space-y-2">
                  {leadData.redFlags?.map((flag, idx) => (
                    <div key={idx} className="bg-white rounded p-3 border border-red-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium text-red-900">{flag.type}</span>
                          <p className="text-sm text-red-700 mt-1">{flag.reason}</p>
                        </div>
                        <span className="text-xs text-red-600">
                          {new Date(flag.addedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 1. Identity & Contact Card */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-[#264a3e] mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('leads.identityContact')}</h3>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.fullName')}</label>
                  <input
                    type="text"
                    value={leadData.name || ""}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.spokenName')}</label>
                  <input
                    type="text"
                    value={leadData.spokenName || ""}
                    onChange={(e) => handleFieldChange("spokenName", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                    placeholder={t('common.ifDifferent')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.phone')}</label>
                  <input
                    type="tel"
                    value={leadData.phone || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const trimmed = v.trim();
                      const normalized = trimmed.length > 0 && trimmed[0] !== "0" && /^[1-9]/.test(trimmed)
                        ? "0" + trimmed
                        : v;
                      handleFieldChange("phone", normalized);
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.bestTimeToCall')}</label>
                  <CustomTimePicker
                    value={leadData.bestTimeToCall || ""}
                    onChange={(v) => handleFieldChange("bestTimeToCall", v)}
                    placeholder={t('common.optional')}
                  />
                </div>
              </div>

              {/* BEX Address Section */}
              <div className="mt-4 pt-4 border-t border-gray-200 bex-address-container">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center flex-wrap gap-2">
                    <MapPin className="w-4 h-4 text-[#264a3e] mr-2" />
                    <h4 className="text-sm font-semibold text-gray-900">{t('leads.bexAddress')}</h4>
                    {showAddressProblematic && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        {t('leads.problematic')}
                      </span>
                    )}
                    {leadData.bexMatched && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {t('leads.verified')}
                      </span>
                    )}
                  </div>
                  {!editingBexAddress ? (
                    <button
                      onClick={() => {
                        bexEditSnapshotRef.current = {
                          municipalitySearch,
                          citySearch,
                          streetSearch,
                          addressNumber,
                          selectedMunicipalityId,
                          selectedSettlementId,
                        };
                        setEditingBexAddress(true);
                      }}
                      className="text-xs px-3 py-1.5 bg-[#264a3e] text-white rounded hover:bg-[#1a3329] flex items-center"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      {t('common.edit')}
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const snap = bexEditSnapshotRef.current;
                          if (snap) {
                            setMunicipalitySearch(snap.municipalitySearch);
                            setCitySearch(snap.citySearch);
                            setStreetSearch(snap.streetSearch);
                            setAddressNumber(snap.addressNumber);
                            setSelectedMunicipalityId(snap.selectedMunicipalityId);
                            setSelectedSettlementId(snap.selectedSettlementId);
                          }
                          setEditingBexAddress(false);
                        }}
                        className="text-xs px-3 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleSaveBexAddress}
                        disabled={savingBexAddress}
                        className="text-xs px-3 py-1.5 bg-[#264a3e] text-white rounded hover:bg-[#1a3329] disabled:opacity-50 flex items-center"
                      >
                        {savingBexAddress ? (
                          <>
                            <Loader className="w-3 h-3 mr-1 animate-spin" />
                            {t('common.saving')}
                          </>
                        ) : (
                          <>
                            <Save className="w-3 h-3 mr-1" />
                            {t('common.save')}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                {!editingBexAddress ? (
                  // Display mode - locked
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.municipality')}</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-black font-semibold">
                          {leadData.area || "—"}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('common.city')}</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-black font-semibold">
                          {leadData.city || "—"}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.zipCode')}</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-black font-semibold">
                          {leadData.bexZipCode || leadData.index || "—"}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.street')}</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-black font-semibold">
                          {(() => {
                            // Priority: streetSearch > bexMatchDetails.street > parsed from address > full address
                            if (streetSearch) {
                              return streetSearch;
                            }
                            if (leadData.bexMatchDetails?.street) {
                              return leadData.bexMatchDetails.street;
                            }
                            if (leadData.address) {
                              // Parse to get just the street name
                              const addressParts = leadData.address.split(", ");
                              const lastPart = addressParts[addressParts.length - 1] || "";
                              const { street } = parseAddressComponents(lastPart);
                              return street || lastPart;
                            }
                            return "—";
                          })()}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.addressNumber')}</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-black font-semibold">
                          {(() => {
                            // Priority: addressNumber > leadData.addr > parsed from address > "—"
                            if (addressNumber) {
                              return addressNumber;
                            }
                            if (leadData.addr) {
                              return leadData.addr;
                            }
                            // Try to parse from address if both are empty
                            if (leadData.address) {
                              const addressParts = leadData.address.split(", ");
                              const lastPart = addressParts.length > 0 ? addressParts[addressParts.length - 1] : "";
                              const { number } = parseAddressComponents(lastPart);
                              if (number) {
                                return number;
                              }
                            }
                            return "—";
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Edit mode - same grid layout as display (3 cols + 2 cols)
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Municipality (Area/Region) - Autocomplete */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('leads.municipality')}
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        readOnly={!bexAutofillBypass}
                        onFocus={() => {
                          setBexAutofillBypass(true);
                          setShowMunicipalityDropdown(true);
                          if (municipalitySearch === null && leadData.area) {
                            setMunicipalitySearch(leadData.area);
                          }
                        }}
                        value={municipalitySearch !== null ? municipalitySearch : (leadData.area || "")}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMunicipalitySearch(value);
                          setShowMunicipalityDropdown(true);
                          const municipality = municipalities.find(m => m.name === value);
                          if (municipality) {
                            setSelectedMunicipalityId(municipality.id);
                            setSelectedSettlementId(null);
                            setCitySearch(null);
                            setStreetSearch(null);
                          } else {
                            setSelectedMunicipalityId(null);
                            setSelectedSettlementId(null);
                            setCitySearch(null);
                            setStreetSearch(null);
                          }
                        }}
                        className="w-full px-3 py-2 text-sm font-bold border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                        placeholder="Type to search municipality..."
                      />
                      {showMunicipalityDropdown && municipalities.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {municipalities
                            .filter(m => fuzzyAddressScore(municipalitySearch || "", m.name) >= FUZZY_MIN_SCORE)
                            .slice(0, 20)
                            .map(municipality => (
                              <button
                                key={municipality.id}
                                type="button"
                                onClick={() => {
                                  setMunicipalitySearch(municipality.name);
                                  setSelectedMunicipalityId(municipality.id);
                                  setSelectedSettlementId(null);
                                  setCitySearch(null);
                                  setStreetSearch(null);
                                  setShowMunicipalityDropdown(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 text-sm"
                              >
                                {municipality.name}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>

                    {/* Settlement (City) - Autocomplete */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('common.city')} ({t('leads.settlement')})
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        readOnly={!bexAutofillBypass}
                        onFocus={() => {
                          setBexAutofillBypass(true);
                          setShowCityDropdown(true);
                          if (citySearch === null && leadData.city) {
                            setCitySearch(leadData.city);
                          }
                        }}
                        value={citySearch !== null ? citySearch : (leadData.city || "")}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCitySearch(value);
                          setShowCityDropdown(true);
                          const settlement = (selectedMunicipalityId ? filteredSettlements : settlements).find(s => s.name === value);
                          if (settlement) {
                            if (selectedMunicipalityId && settlement.municipalityId === selectedMunicipalityId) {
                              setSelectedSettlementId(settlement.id);
                              setStreetSearch(null);
                            } else if (!selectedMunicipalityId) {
                              // City-first: auto-inherit municipality from picked city
                              const mun = municipalities.find(m => m.id === settlement.municipalityId);
                              if (mun) {
                                setMunicipalitySearch(mun.name);
                                setSelectedMunicipalityId(mun.id);
                              }
                              setSelectedSettlementId(settlement.id);
                              setStreetSearch(null);
                            } else {
                              setSelectedSettlementId(null);
                            }
                          } else {
                            setSelectedSettlementId(null);
                          }
                        }}
                        className="w-full px-3 py-2 text-sm font-bold border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                        placeholder="Type to search city..."
                      />
                      {!selectedMunicipalityId && (
                        <p className="mt-1 text-xs text-gray-500">Pick city first — municipality will be auto-filled</p>
                      )}
                      {showCityDropdown && (() => {
                        const q = citySearch || "";
                        // When no municipality: show all matching settlements (city-first flow)
                        const sameMun = selectedMunicipalityId
                          ? filteredSettlements.filter(s => fuzzyAddressScore(q, s.name) >= FUZZY_MIN_SCORE).slice(0, 20)
                          : settlements.filter(s => fuzzyAddressScore(q, s.name) >= FUZZY_MIN_SCORE).slice(0, 20);
                        const otherMun = selectedMunicipalityId && q.trim()
                          ? settlements
                              .filter(s => s.municipalityId !== selectedMunicipalityId && fuzzyAddressScore(q, s.name) >= FUZZY_MIN_SCORE)
                              .slice(0, 20)
                          : [];
                        if (sameMun.length === 0 && otherMun.length === 0) return null;
                        return (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {sameMun.length > 0 && (
                            <>
                              {selectedMunicipalityId && (
                                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100 sticky top-0">
                                  {t('callCenter.inCurrentMunicipality')}
                                </div>
                              )}
                              {sameMun.map(settlement => {
                                const mun = municipalities.find(m => m.id === settlement.municipalityId);
                                return (
                                <button
                                  key={settlement.id}
                                  type="button"
                                  onClick={() => {
                                    if (!selectedMunicipalityId && mun) {
                                      justSelectedOtherMunicipalityRef.current = true;
                                      setMunicipalitySearch(mun.name);
                                      setSelectedMunicipalityId(mun.id);
                                    }
                                    setCitySearch(settlement.name);
                                    setSelectedSettlementId(settlement.id);
                                    setStreetSearch(null);
                                    setShowCityDropdown(false);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 text-sm"
                                >
                                  {settlement.name} ({settlement.zip}){!selectedMunicipalityId && mun ? ` — ${mun.name}` : ""}
                                </button>
                                );
                              })}
                            </>
                          )}
                          {otherMun.length > 0 && (
                            <>
                              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100 sticky top-0">
                                {t('callCenter.sameCityInOtherMunicipality')}
                              </div>
                              {otherMun.map(settlement => {
                                const mun = municipalities.find(m => m.id === settlement.municipalityId);
                                return (
                                  <button
                                    key={`other-${settlement.id}`}
                                    type="button"
                                    onClick={() => {
                                      justSelectedOtherMunicipalityRef.current = true;
                                      if (mun) {
                                        setMunicipalitySearch(mun.name);
                                        setSelectedMunicipalityId(mun.id);
                                      }
                                      setCitySearch(settlement.name);
                                      setSelectedSettlementId(settlement.id);
                                      setStreetSearch(null);
                                      setShowCityDropdown(false);
                                    }}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 text-sm"
                                  >
                                    {settlement.name} ({settlement.zip}) — {mun?.name ?? ""}
                                  </button>
                                );
                              })}
                            </>
                          )}
                        </div>
                        );
                      })()}
                    </div>

                    {/* Zip Code (Auto-filled) - use full settlements so zip updates when selecting "same city in other municipality" */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.zipCode')}</label>
                      <input
                        type="text"
                        autoComplete="off"
                        value={selectedSettlementId 
                          ? (settlements.find(s => s.id === selectedSettlementId)?.zip || leadData.index || "")
                          : (leadData.bexZipCode || leadData.index || "")
                        }
                        readOnly
                        className="w-full px-3 py-2 text-sm font-bold border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                        placeholder="Auto-filled from city"
                      />
                    </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Street - Autocomplete */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t('leads.street')}</label>
                      <input
                        type="text"
                        autoComplete="off"
                        readOnly={!bexAutofillBypass}
                        value={(() => {
                          // Priority: streetSearch > bexMatchDetails.street > parsed from address
                          if (streetSearch !== null) {
                            return streetSearch;
                          }
                          if (leadData.bexMatchDetails?.street) {
                            return leadData.bexMatchDetails.street;
                          }
                          if (leadData.address) {
                            // Parse to get just the street name
                            const addressParts = leadData.address.split(", ");
                            const lastPart = addressParts[addressParts.length - 1] || "";
                            const { street } = parseAddressComponents(lastPart);
                            return street || lastPart;
                          }
                          return "";
                        })()}
                        onChange={(e) => {
                          const value = e.target.value;
                          setStreetSearch(value);
                          setShowStreetDropdown(true);
                        }}
                        onFocus={() => {
                          setBexAutofillBypass(true);
                          if (selectedSettlementId) {
                            setShowStreetDropdown(true);
                            if (streetSearch === null) {
                              if (leadData.bexMatchDetails?.street) {
                                setStreetSearch(leadData.bexMatchDetails.street);
                              } else if (leadData.address) {
                                const addressParts = leadData.address.split(", ");
                                const lastPart = addressParts[addressParts.length - 1] || "";
                                const { street } = parseAddressComponents(lastPart);
                                setStreetSearch(street || lastPart);
                              }
                            }
                          }
                        }}
                        disabled={!selectedSettlementId}
                        className="w-full px-3 py-2 text-sm font-bold border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent disabled:bg-gray-200 disabled:text-gray-800 disabled:cursor-not-allowed"
                        placeholder={selectedSettlementId ? "Type to search street..." : "Select city first"}
                      />
                      {!selectedSettlementId && (
                        <p className="mt-1 text-xs text-gray-500">Select city first</p>
                      )}
                      {showStreetDropdown && selectedSettlementId && filteredStreets.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {filteredStreets
                            .filter(s => fuzzyAddressScore(streetSearch || "", s.name) >= FUZZY_MIN_SCORE)
                            .slice(0, 20)
                            .map(street => (
                              <button
                                key={street.id}
                                type="button"
                                onClick={() => {
                                  setStreetSearch(street.name);
                                  setShowStreetDropdown(false);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 text-sm"
                              >
                                {street.name}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>

                    {/* Address Number */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('leads.addressNumber')}
                        <span className="text-xs text-gray-500 ml-1">({t('leads.addressNumberFormat')})</span>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        readOnly={!bexAutofillBypass}
                        onFocus={() => setBexAutofillBypass(true)}
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        className="w-full px-3 py-2 text-sm font-bold border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                        placeholder="e.g., 5 or 5/10"
                      />
                    </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Previous Orders - Below BEX Address (shipping status: In Transit, Delivered, Canceled only); latest first */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#264a3e] shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('leads.previousOrders')}</h3>
                    <p className="text-xs text-gray-500">{t('leads.latestOrderFirst')}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">({orderHistory.length})</span>
              </div>
              <div className="max-h-96 overflow-y-auto space-y-4">
                {orderHistory.length > 0 ? (
                  [...orderHistory]
                    .sort((a, b) => (b.time ?? 0) - (a.time ?? 0))
                    .map((order: any, idx: number) => {
                    const orderProducts = order.products || [];
                    const statusNum = order.status ?? 6;
                    const inTransit = statusNum >= 6 && statusNum <= 9;
                    const delivered = statusNum === 10;
                    const canceled = statusNum === 5 || statusNum === 11;
                    const hasTracking = !!(order.trackingId && String(order.trackingId).trim());
                    const orderRejected = order.rejected === true;
                    const orderNeedsConfirmation = order.needsConfirmation === true;
                    const shippingLabel = orderRejected
                      ? t('leads.orderRejected')
                      : orderNeedsConfirmation
                        ? t('leads.unconfirmed')
                        : !hasTracking
                          ? t('leads.ready')
                          : inTransit
                            ? t('leads.inTransit')
                            : delivered
                              ? t('leads.delivered')
                              : canceled
                                ? (order.cancelComment ? `${t('leads.canceled')}: ${order.cancelComment}` : t('leads.canceled'))
                                : t('leads.inTransit');
                    const shippingColor = orderRejected
                      ? "bg-red-100 text-red-800"
                      : orderNeedsConfirmation
                        ? "bg-amber-100 text-amber-800"
                        : !hasTracking
                          ? "bg-gray-100 text-gray-800"
                          : inTransit
                            ? "bg-blue-100 text-blue-800"
                            : delivered
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800";
                    const resolveProductImageUrl = (good: any): string | null => {
                      const id = good?.id ?? good?.productId;
                      const p = id ? products.find((pr: Product) => String(pr.id) === String(id)) : null;
                      const img = p?.image || good?.mainImage || good?.image || good?.main_image;
                      return img ? (img.startsWith("http") ? img : `https://naturatherapy.mk/image/cache/${(img as string).replace(/^catalog\//, "")}`) : null;
                    };
                    const resolveImageByProductId = (productId: string): string | null => {
                      const p = products.find((pr: Product) => String(pr.id) === String(productId));
                      const img = p?.image;
                      return img ? (img.startsWith("http") ? img : `https://naturatherapy.mk/image/cache/${(img as string).replace(/^catalog\//, "")}`) : null;
                    };
                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-r from-white to-gray-50">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-base text-gray-900">
                            {t('common.order')} #{order.id || order.orderId}
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${shippingColor}`}>
                            {shippingLabel}
                          </span>
                        </div>

                        {/* Every product in the order with its own image (no order-level image; images from products list) */}
                        <div className="mb-3">
                          {orderProducts.length > 0 ? (
                            <div className="space-y-3">
                              {orderProducts.map((p: any, pIdx: number) => {
                                const qty = p.count ?? p.quantity ?? 1;
                                const name = p.name || p.productName || "";
                                const productImageUrl = resolveProductImageUrl(p);
                                const productIds = p.productIds;
                                const includedFromOrderItems = p.includedProducts;
                                const includedWithImages: Array<{ productName: string; imageUrl: string | null }> = includedFromOrderItems?.length
                                  ? (includedFromOrderItems as Array<{ productId?: string; productName?: string; name?: string }>).map((ip: any) => ({
                                      productName: ip.productName || ip.name || (ip.productId ? (products.find((pr: Product) => String(pr.id) === String(ip.productId))?.name ?? "") : "") || "",
                                      imageUrl: ip.productId ? resolveImageByProductId(ip.productId) : null,
                                    }))
                                    : productIds?.length
                                    ? (productIds as string[]).map((pid: string) => {
                                        const pr = products.find((pr0: Product) => String(pr0.id) === String(pid));
                                        return { productName: pr?.name ?? "", imageUrl: resolveImageByProductId(pid) };
                                      })
                                    : [];
                                return (
                                  <div key={pIdx} className="flex items-start gap-3 text-sm text-gray-700">
                                    {productImageUrl ? (
                                      <img
                                        src={productImageUrl}
                                        alt={name}
                                        className="w-6 h-6 flex-shrink-0 object-cover rounded border border-gray-200"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <div className="w-6 h-6 flex-shrink-0 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                        <Package className="w-3 h-3 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium">
                                        {name}
                                        {qty > 1 && <span className="text-gray-500 ml-1">× {qty}</span>}
                                      </div>
                                      {includedWithImages.length > 0 && (
                                        <div className="mt-0.5 pl-2 border-l border-gray-300 text-xs text-gray-600 space-y-1">
                                          <span className="font-medium">{t('common.includes')}:</span>
                                          {includedWithImages.map((inc, i) => (
                                            <div key={i} className="flex items-center gap-1.5">
                                              {inc.imageUrl ? (
                                                <img
                                                  src={inc.imageUrl}
                                                  alt={inc.productName}
                                                  className="w-6 h-6 flex-shrink-0 object-cover rounded border border-gray-200"
                                                  onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = "none";
                                                  }}
                                                />
                                              ) : (
                                                <div className="w-6 h-6 flex-shrink-0 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                                  <Package className="w-3 h-3 text-gray-400" />
                                                </div>
                                              )}
                                              <span>{inc.productName}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600">{order.offername || "—"}</p>
                          )}
                        </div>

                        {/* Date and Total */}
                        <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                          <div className="flex items-center text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            <span>{order.time ? formatTimestampFull(order.time) : "—"}</span>
                          </div>
                          <div className="flex items-center font-semibold text-[#264a3e]">
                            <span>{order.price ? `${order.price.toLocaleString()} ${(order.currency || "RSD").toUpperCase()}` : "—"}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">{t('common.noPreviousOrders')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Info Section */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-[#264a3e] mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">{t('leads.profileInfo')}</h3>
              </div>
              
              {/* General Notes */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-2">{t('leads.generalNotes')}</label>
                <textarea
                  value={generalNotes}
                  onChange={(e) => setGeneralNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                  placeholder={t('leads.enterGeneralNotes')}
                />
              </div>

              {/* Health Info */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-medium text-gray-700">{t('leads.healthInfo')}</label>
                  <button
                    onClick={handleAddHealthNote}
                    className="text-xs px-2 py-1 bg-[#264a3e] text-white rounded hover:bg-[#1a3329] flex items-center"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {t('common.addNew')}
                  </button>
                </div>
                <div className="space-y-2 mb-3">
                  {healthInfo.map((note) => (
                    <div key={note.id} className="flex items-start justify-between p-2 bg-gray-50 rounded border border-gray-200">
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{note.note}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(note.addedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveHealthNote(note.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  type="text"
                  value={newHealthNote}
                  onChange={(e) => setNewHealthNote(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddHealthNote();
                    }
                  }}
                  placeholder={t('leads.addHealthNote')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                />
              </div>

            </div>
          </div>

          {/* Right Column - Order needing confirmation (when successful_with_confirmation) or Previous status list + Create New Order */}
          <div className="lg:col-span-1 space-y-4">
            {/* Interested Items - only for new leads (interestedForProduct + externalHistory.matchedProduct, unique) */}
            {interestedProducts.length > 0 && (
              <div className="bg-white shadow rounded-lg p-4 border border-[#264a3e]/20">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5 text-[#264a3e] shrink-0" />
                  <h3 className="text-base font-semibold text-gray-900">{t('callCenter.interestedItems')}</h3>
                </div>
                <div className="space-y-3">
                  {interestedProducts.map((item) => {
                    const p = products.find((pr: Product) => String(pr.id) === String(item.productId));
                    const img = p?.image || productImagesMap[item.productId];
                    const imageUrl = img ? (img.startsWith("http") ? img : `https://naturatherapy.mk/image/cache/${(img as string).replace(/^catalog\//, "")}`) : null;
                    return (
                      <div key={item.productId} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.productName}
                            className="w-14 h-14 flex-shrink-0 object-cover rounded-md border border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-14 h-14 flex-shrink-0 bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center">
                            <Package className="w-7 h-7 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* When lead has order needing confirmation: show that order for confirmation instead of Previous status list */}
            {/* When lead has order needing confirmation: compact info banner only; basket is in Create New Order below (editable, add/remove with delete confirmation) */}
            {leadData?.lastOrderNeedsConfirmation && orderNeedingConfirmation ? (
              <div className="bg-white shadow rounded-lg p-4 border-2 border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-amber-600 shrink-0" />
                  <h3 className="text-base font-semibold text-gray-900">{t('callCenter.orderNeedingConfirmation')}</h3>
                </div>
                {leadData.lastOrderConfirmOnDate && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                    {t('orders.confirmOnDate')}: {new Date(leadData.lastOrderConfirmOnDate + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  </p>
                )}
                <p className="text-sm text-gray-500 mb-3">
                  {t('callCenter.orderNeedingConfirmationHint')}
                </p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowConfirmOrderModal(true)}
                    disabled={confirmingOrder}
                    className="w-full px-3 py-2 border border-transparent bg-[#264a3e] text-white rounded-md hover:bg-[#1e3a32] disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {confirmingOrder ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {t('callCenter.confirmOrder')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRejectOrderModal(true)}
                    disabled={rejectingOrder}
                    className="w-full px-3 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {rejectingOrder ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        {t('callCenter.rejectOrder')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (() => {
              const endCallActions = (leadData?.operatorActions || []).filter((e) => e.action === 'end_call');
              if (endCallActions.length === 0) return null;
              return (
              <div className="bg-white shadow rounded-lg p-6" key="previous-status-list">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{t('callCenter.previousStatusList')}</h3>
                  <button
                    type="button"
                    onClick={() => setOperatorActionsSortOrder((o) => (o === 'newest' ? 'oldest' : 'newest'))}
                    className="text-sm text-[#264a3e] hover:underline"
                  >
                    {operatorActionsSortOrder === 'newest' ? t('callCenter.sortOldestFirst') : t('callCenter.sortNewestFirst')}
                  </button>
                </div>
                <div className="space-y-3 max-h-[450px] overflow-y-auto">
                  {[...endCallActions]
                      .sort((a, b) => {
                        const ta = new Date(a.at).getTime();
                        const tb = new Date(b.at).getTime();
                        return operatorActionsSortOrder === 'newest' ? tb - ta : ta - tb;
                      })
                      .map((entry, idx) => (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-lg p-3 text-sm"
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">
                              {t('callCenter.actionEndCall')}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {new Date(entry.at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          </div>
                          {entry.completedBy?.username && (
                            <p className="text-xs text-gray-600 mt-1">
                              {t('common.user')}: {entry.completedBy.username}
                            </p>
                          )}
                          <div className="mt-2 pt-2 border-t border-gray-100 space-y-1 text-xs">
                              {entry.callOutcome && (
                                <p>
                                  <span className="text-gray-500">{t('common.callOutcome')}: </span>
                                  {entry.callOutcome === 'successful' && t('leads.successful')}
                                  {entry.callOutcome === 'successful_with_confirmation' && t('leads.successfulWithConfirmation')}
                                  {entry.callOutcome === 'no_answer' && t('leads.noAnswer')}
                                  {entry.callOutcome === 'busy_line' && t('leads.busyLine')}
                                  {entry.callOutcome === 'wrong_number' && t('leads.wrongNumber')}
                                  {entry.callOutcome === 'unreachable' && t('leads.unreachable')}
                                  {entry.callOutcome === 'number_doesnt_exist' && t('leads.numberDoesntExist')}
                                  {entry.callOutcome === 'not_interested' && t('leads.notInterested')}
                                  {entry.callOutcome === 'callback' && t('leads.callback')}
                                  {entry.callOutcome === 'other' && t('leads.other')}
                                  {!['successful', 'successful_with_confirmation', 'no_answer', 'busy_line', 'wrong_number', 'unreachable', 'number_doesnt_exist', 'not_interested', 'callback', 'other'].includes(entry.callOutcome) && entry.callOutcome}
                                </p>
                              )}
                              {entry.callOutcome === 'successful_with_confirmation' && (
                                <p>
                                  <span className="text-gray-500">{t('callCenter.confirmationOutcome')}: </span>
                                  {(() => {
                                    const isCurrentCall = leadData?.callCenterCompletedAt && new Date(entry.at).getTime() === new Date(leadData.callCenterCompletedAt).getTime();
                                    if (!isCurrentCall) return <span className="text-gray-400">—</span>;
                                    if (leadData?.lastOrderNeedsConfirmation) return <span className="text-gray-400">—</span>;
                                    if (leadData?.lastOrderRejected) return <span className="text-red-700 font-medium">{t('callCenter.confirmationRejected')}</span>;
                                    return <span className="text-green-700 font-medium">{t('callCenter.confirmationSuccess')}</span>;
                                  })()}
                                </p>
                              )}
                              {entry.notInterestedReason && (
                                <p>
                                  <span className="text-gray-500">{t('leads.notInterestedReason')}: </span>
                                  {entry.notInterestedReason === 'no_money' && t('leads.notInterestedReasonNoMoney')}
                                  {entry.notInterestedReason === 'still_has_product' && t('leads.notInterestedReasonStillHasProduct')}
                                  {entry.notInterestedReason === 'product_doesnt_help' && t('leads.notInterestedReasonProductDoesntHelp')}
                                  {entry.notInterestedReason === 'do_not_call_again' && t('leads.notInterestedReasonDoNotCallAgain')}
                                  {entry.notInterestedReason === 'because_of_death' && t('leads.notInterestedReasonBecauseOfDeath')}
                                  {entry.notInterestedReason === 'orders_for_someone_else' && t('leads.notInterestedReasonOrdersForSomeoneElse')}
                                  {entry.notInterestedReason === 'not_interested_not_interested' && t('leads.notInterestedReasonNotInterested')}
                                  {entry.notInterestedReason === 'other' && t('leads.notInterestedReasonOther')}
                                  {!['no_money', 'still_has_product', 'product_doesnt_help', 'do_not_call_again', 'because_of_death', 'orders_for_someone_else', 'not_interested_not_interested', 'other'].includes(entry.notInterestedReason) && entry.notInterestedReason}
                                </p>
                              )}
                              {entry.callNotes?.trim() && (
                                <p className="text-gray-700">{entry.callNotes.trim()}</p>
                              )}
                              {entry.followUpDate && (
                                <p>
                                  <span className="text-gray-500">{t('common.followUpDate')}: </span>
                                  {entry.followUpDate}
                                  {entry.followUpHours && ` ${entry.followUpHours}`}
                                </p>
                              )}
                          </div>
                        </div>
                      ))}
                </div>
              </div>
              );
            })()}
            {((leadData?.callOutcome === "no_answer" || leadData?.callOutcome === "callback") &&
              (leadData?.followUpNotes?.trim() || leadData?.followUpPotentialProduct?.trim())) && (
              <div className="bg-white shadow rounded-lg p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-900">{t("callCenter.callbackDetails")}</h3>
                </div>
                <div className="space-y-2 text-sm">
                  {leadData.followUpNotes?.trim() && (
                    <div className="break-words">
                      <span className="text-gray-500">{t("callCenter.followUpMessage")}: </span>
                      <span className="text-gray-800">{leadData.followUpNotes.trim()}</span>
                    </div>
                  )}
                  {leadData.followUpPotentialProduct?.trim() && (
                    <div className="break-words">
                      <span className="text-gray-500">{t("callCenter.nextCallProduct")}: </span>
                      <span className="text-gray-800">{leadData.followUpPotentialProduct.trim()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white shadow rounded-lg p-6">
              {showAddressProblematic && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-red-800">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm font-medium">{t('callCenter.fixAddressToCreateOrder')}</p>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <ShoppingCart className="w-5 h-5 text-[#264a3e] mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">{t('leads.createNewOrder')}</h3>
                </div>
                {orderItems.length > 0 && (
                  <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded">{t('common.draft')}</span>
                )}
              </div>

              {/* Special Offers Section */}
              {offers.length > 0 && (
                <div className="mb-4 border-b border-gray-200 pb-4">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-2">
                    <Tag className="w-4 h-4 mr-1 text-purple-600" />
                    Special Offers
                  </label>
                  <div className="space-y-2">
                    {offers.map((offer) => {
                      // Check if special offer has enough products in stock
                      let isOutOfStock = false;
                      let inStockCount = 0;
                      let outOfStockReason = "";
                      
                      if (offer.type === 'special' && offer.availableProducts && offer.maxPickCount) {
                        // Count how many available products are in stock
                        inStockCount = offer.availableProducts.filter((ap) => {
                          const product = products.find(p => 
                            p.id === ap.productId || 
                            p.id === String(ap.productId) ||
                            String(p.id) === String(ap.productId)
                          );
                          if (!product) return false;
                          const availableStock = product.type === "bundle"
                            ? (product.estimatedStock || 0)
                            : (product.quantity || 0);
                          return availableStock > 0;
                        }).length;
                        
                        // Check if we have enough products in stock to meet the minimum selection requirement
                        isOutOfStock = inStockCount < offer.maxPickCount;
                        if (isOutOfStock) {
                          outOfStockReason = `Only ${inStockCount} product(s) in stock (need ${offer.maxPickCount})`;
                        }
                      } else if (offer.type === 'gift') {
                        // Check if base products (bundles) are in stock
                        if (offer.baseProducts && offer.baseProducts.length > 0) {
                          for (const baseProduct of offer.baseProducts) {
                            const product = products.find(p => 
                              p.id === baseProduct.productId || 
                              p.id === String(baseProduct.productId) ||
                              String(p.id) === String(baseProduct.productId)
                            );
                            
                            if (!product) {
                              isOutOfStock = true;
                              outOfStockReason = `${baseProduct.productName} not found`;
                              break;
                            }
                            
                            // Check stock based on product type
                            const availableStock = product.type === "bundle"
                              ? (product.estimatedStock || 0)
                              : (product.quantity || 0);
                            
                            if (availableStock <= 0) {
                              isOutOfStock = true;
                              outOfStockReason = `${baseProduct.productName} is out of stock`;
                              break;
                            }
                          }
                        }
                        
                        // Check if gift items are in stock
                        if (!isOutOfStock && offer.giftItems && offer.giftItems.length > 0) {
                          for (const giftItem of offer.giftItems) {
                            const product = products.find(p => 
                              p.id === giftItem.productId || 
                              p.id === String(giftItem.productId) ||
                              String(p.id) === String(giftItem.productId)
                            );
                            
                            if (!product) {
                              isOutOfStock = true;
                              outOfStockReason = `Gift item ${giftItem.productName} not found`;
                              break;
                            }
                            
                            // Check stock based on product type
                            const availableStock = product.type === "bundle"
                              ? (product.estimatedStock || 0)
                              : (product.quantity || 0);
                            
                            if (availableStock < giftItem.quantity) {
                              isOutOfStock = true;
                              outOfStockReason = `Gift item ${giftItem.productName}: only ${availableStock} available (need ${giftItem.quantity})`;
                              break;
                            }
                          }
                        }
                      }
                      
                      const offerId = offer.id || offer._id || '';
                      const isInBasket = selectedOffers.has(offerId);
                      // Only show "Selecting" stage for special offers, not gift offers
                      const isActiveForSelection = offer.type === 'special' && selectedOffer && (selectedOffer.id === offer.id || selectedOffer._id === offer._id);
                      
                      return (
                      <div
                        key={offer.id || offer._id}
                        className={`relative border rounded-lg p-3 transition-all ${
                          isOutOfStock
                            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            : isActiveForSelection
                            ? 'border-gray-300 bg-gray-50 cursor-pointer'
                            : isInBasket
                            ? 'border-gray-300 bg-gray-50 cursor-pointer'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                        }`}
                        onClick={() => !isOutOfStock && handleSelectOffer(offer)}
                      >
                        {/* Offer Badge - Floating Ribbon */}
                        {isInBasket && (
                          <div className="absolute top-0 right-0 bg-gray-700 text-white text-xs font-medium px-2.5 py-0.5 rounded-bl-lg rounded-tr-lg z-10">
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              In Basket
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-start justify-between pr-20">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              {offer.type === 'special' ? (
                                <Tag className="w-4 h-4 text-gray-500" />
                              ) : (
                                <Gift className="w-4 h-4 text-gray-500" />
                              )}
                              <h4 className="text-sm font-medium text-gray-900">{offer.name}</h4>
                              {isOutOfStock && (
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                                  Out of Stock
                                </span>
                              )}
                            </div>
                            {offer.description && (
                              <p className="text-xs text-gray-600 mb-2">{offer.description}</p>
                            )}
                            {offer.type === 'special' && offer.availableProducts && offer.maxPickCount && offer.specialPrice && (
                              <div className="text-xs text-gray-600 space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-700">Select {offer.maxPickCount} product{offer.maxPickCount > 1 ? 's' : ''} from {offer.availableProducts.length} available</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Special Price:</span>
                                  <span className="text-gray-900 font-medium">{offer.specialPrice.toLocaleString()} RSD</span>
                                </div>
                                {isOutOfStock && (
                                  <div className="mt-1 text-red-600 font-medium">
                                    {outOfStockReason || `Only ${inStockCount} product(s) in stock (need ${offer.maxPickCount})`}
                                  </div>
                                )}
                              </div>
                            )}
                            {offer.type === 'gift' && offer.baseProducts && offer.giftItems && (
                              <div className="text-xs text-gray-600">
                                <span>{offer.baseProducts.length} base product(s) + {offer.giftItems.length} gift(s)</span>
                                {isOutOfStock && (
                                  <div className="mt-1 text-red-600 font-medium">
                                    {outOfStockReason || "Out of Stock"}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {!isOutOfStock && isActiveForSelection && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-gray-600 font-medium">Selecting...</span>
                              <CheckCircle className="w-4 h-4 text-gray-600" />
                            </div>
                          )}
                        </div>
                        
                        {/* Special Offer Product Selection - Only show for the selected special offer */}
                        {!isOutOfStock && 
                         selectedOffer && 
                         selectedOffer.type === 'special' &&
                         (selectedOffer.id === offer.id || selectedOffer._id === offer._id) && 
                         offer.type === 'special' && 
                         offer.availableProducts &&
                         offer.maxPickCount && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-gray-900 mb-1">
                                Select {offer.maxPickCount} product{offer.maxPickCount > 1 ? 's' : ''}:
                              </p>
                              <p className="text-xs text-gray-500">
                                {selectedOfferProducts.length} of {offer.maxPickCount} selected
                              </p>
                            </div>
                            <div className="space-y-2">
                              {offer.availableProducts.map((ap) => {
                                const product = products.find(p => p.id === ap.productId);
                                if (!product) return null;
                                
                                // Check stock based on product type
                                const availableStock = product.type === "bundle"
                                  ? (product.estimatedStock || 0)
                                  : (product.quantity || 0);
                                
                                const isInStock = availableStock > 0;
                                const isSelected = selectedOfferProducts.includes(ap.productId);
                                
                                if (!isInStock) return null; // Don't show out of stock products
                                
                                return (
                                  <button
                                    key={ap.productId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectOfferProduct(ap.productId);
                                    }}
                                    className={`w-full flex items-center p-2 rounded text-left transition-all ${
                                      isSelected
                                        ? 'bg-gray-100 border border-gray-300'
                                        : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center flex-shrink-0 ${
                                      isSelected 
                                        ? 'border-gray-600 bg-gray-600' 
                                        : 'border-gray-400 bg-white'
                                    }`}>
                                      {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-xs font-medium mb-0.5 ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                        {ap.productName}
                                      </p>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-xs ${isSelected ? 'text-gray-600' : 'text-gray-500'}`}>
                                          {ap.price.toLocaleString()} RSD
                                        </span>
                                        <span className={`text-xs px-1 py-0.5 rounded ${
                                          availableStock > 0 
                                            ? 'bg-gray-100 text-gray-700 border border-gray-300'
                                            : 'bg-red-50 text-red-700 border border-red-200'
                                        }`}>
                                          {availableStock > 0 
                                            ? `Stock: ${availableStock}` 
                                            : 'Out of Stock'}
                                        </span>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Product Selection - Always visible */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">{t('common.addProduct')}</label>
                <div className="border border-gray-200 rounded-md max-h-[400px] overflow-y-auto">
                  {products.length > 0 ? (
                    products.map((product) => {
                      // Check stock based on product type
                      const isOutOfStock = product.type === "bundle" 
                        ? (product.estimatedStock || 0) <= 0
                        : (product.quantity || 0) <= 0;
                      
                      const stockDisplay = product.type === "bundle"
                        ? (product.estimatedStock || 0)
                        : (product.quantity || 0);
                      
                      return (
                        <button
                          key={product.id}
                          onClick={() => !isOutOfStock && handleAddProduct(product)}
                          disabled={isOutOfStock}
                          className={`w-full flex items-center p-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                            isOutOfStock ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded mr-2"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center mr-2">
                              <ImageIcon className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 text-left">
                            <div className="flex items-center space-x-1">
                              <p className="text-xs font-medium text-gray-900">{product.name}</p>
                              {product.type === "bundle" && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                                  Bundle
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <p className="text-xs text-gray-500">{product.price} {product.currency}</p>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                stockDisplay > 0 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {stockDisplay > 0 
                                  ? `Stock: ${stockDisplay}` 
                                  : 'Out of Stock'}
                              </span>
                            </div>
                          </div>
                          {!isOutOfStock && <Plus className="w-4 h-4 text-[#264a3e]" />}
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-xs text-gray-500 p-2">{t('common.loadingProducts')}</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              {orderItems.length > 0 && (
                <div className="mb-4 border-t border-gray-200 pt-4 mt-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">{t('common.orderItems')}</label>
                  <div className="space-y-3">
                    {orderItems.map((item, idx) => {
                      const originalProduct = products.find((p) => p.id === item.productId);
                      const originalPrice = originalProduct?.price || item.unitPrice;
                      const discountPercent = originalPrice > 0 ? ((originalPrice - item.unitPrice) / originalPrice) * 100 : 0;
                      const needsNote = discountPercent >= 70 || item.unitPrice === 0; // Also need note if price is 0 (gift items)
                      const isSpecialOffer = item.isSpecialOffer || false;
                      
                      // Auto-fill "Gift" for items with price 0 if not already set
                      const discountNote = item.unitPrice === 0 && !item.discountNote ? "Gift" : (item.discountNote || "");

                      return (
                        <div key={idx} className={`relative border rounded-md p-3 pt-8 ${isSpecialOffer ? 'border-gray-300 bg-gray-50' : 'border-gray-200'}`}>
                          {/* Special Offer Badge - Floating Ribbon */}
                          {isSpecialOffer && (
                            <div className="absolute top-0 right-0 bg-gray-700 text-white text-xs font-medium px-2.5 py-0.5 rounded-bl-md rounded-tr-md z-10">
                              Special Offer
                            </div>
                          )}
                          
                          <div className="flex items-start mb-2">
                            {/* No image/icon for special offers */}
                            { !isSpecialOffer && item.image && (
                              <img
                                src={item.image}
                                alt={item.productName}
                                className="w-10 h-10 object-cover rounded mr-3"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-semibold text-gray-900">{item.productName}</p>
                              </div>
                              
                              {/* Show included products for special offers */}
                              {isSpecialOffer && item.includedProducts && item.includedProducts.length > 0 && (
                                <div className="mt-2 mb-2 pl-3 border-l border-gray-300">
                                  <p className="text-xs font-medium text-gray-700 mb-1">Included products:</p>
                                  <ul className="text-xs text-gray-600 space-y-1.5">
                                    {item.includedProducts.map((included, i) => (
                                      <li key={i} className="flex items-center gap-2">
                                        {included.image && (
                                          <img
                                            src={included.image}
                                            alt={included.productName}
                                            className="w-6 h-6 min-w-[24px] object-cover rounded flex-shrink-0"
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = "none";
                                            }}
                                          />
                                        )}
                                        <span>{included.productName}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              <div className="flex items-center gap-2 mt-1">
                                <button
                                  onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                                  className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="text-xs text-gray-700 w-8 text-center">{item.quantity}</span>
                                <button
                                  onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                                  className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                {isSpecialOffer && (
                                  <span className="text-xs text-gray-500 ml-1">(Special Offer)</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => setPendingDeleteItemIndex(idx)}
                              className="text-red-500 hover:text-red-700"
                              title={t('common.removeItem')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-2">
                            <label className="block text-xs text-gray-700 mb-1">{t('common.price')} ({t('common.perUnit')})</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.unitPrice}
                              onChange={(e) =>
                                handleUpdatePrice(item.productId, parseFloat(e.target.value) || 0, originalPrice)
                              }
                              disabled
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                            />
                            {needsNote && (
                              <div className="mt-1">
                                <label className="block text-xs text-red-700 mb-1">{t('common.discountNoteRequired')}</label>
                                <input
                                  type="text"
                                  value={discountNote}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setOrderItems(
                                      orderItems.map((i) =>
                                        i.productId === item.productId
                                          ? { ...i, discountNote: newValue }
                                          : i
                                      )
                                    );
                                  }}
                                  placeholder={t('common.discountNotePlaceholder')}
                                  className="w-full px-2 py-1 text-xs border border-red-300 rounded-md focus:ring-2 focus:ring-red-500"
                                />
                              </div>
                            )}
                            <p className="text-xs text-gray-600 mt-1">
                              {t('common.total')}: <span className="font-semibold text-[#264a3e]">{item.totalPrice.toFixed(2)} {item.currency}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Order Total */}
              {orderItems.length > 0 && (
                <div className="border-t border-gray-200 pt-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-900">{t('common.total')}:</span>
                    <span className="text-lg font-bold text-[#264a3e]">
                      {totalOrderValue.toFixed(2)} {orderItems[0]?.currency || "RSD"}
                    </span>
                  </div>
                </div>
              )}

              {/* Shipping comment and send date (weekdays only, today or future) */}
              {orderItems.length > 0 && (
                <div className="border-t border-gray-200 pt-4 mb-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.shippingComment')}</label>
                    <textarea
                      value={shippingComment}
                      onChange={(e) => setShippingComment(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                      placeholder={t('orders.shippingCommentPlaceholder')}
                    />
                  </div>
                  {SHOW_ORDER_SEND_DATE_PICKER && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.requestedSendDate')}</label>
                      <SendDatePicker
                        value={requestedSendDate}
                        onChange={setRequestedSendDate}
                        minDate={getNextWorkingDateAsDate()}
                      />
                      <p className="mt-1 text-xs text-gray-500">{t('orders.weekdaysOnly')}</p>
                      {requestedSendDate && isPotentialOrderDate(requestedSendDate) && (
                        <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                          {t('orders.potentialOrderHint', {
                            date: new Date(requestedSendDate + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }),
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Need confirmation: when creating order, operator can mark that order must be confirmed on a date; shipping = next day after that */}
              {orderItems.length > 0 && (
                <div className="border-t border-gray-200 pt-4 mb-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={needsConfirmation}
                      onChange={(e) => {
                        setNeedsConfirmation(e.target.checked);
                        if (!e.target.checked) setConfirmOnDate("");
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                    />
                    <span className="text-sm font-medium text-gray-700">{t('orders.needConfirmation')}</span>
                  </label>
                  {needsConfirmation && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('orders.confirmOnDate')} <span className="text-red-500">*</span>
                      </label>
                      <SendDatePicker
                        value={confirmOnDate}
                        onChange={setConfirmOnDate}
                        minDate={new Date()}
                        allowSaturdayOnly
                      />
                    </div>
                  )}
                </div>
              )}

              {/* End Call Button - opens modal; disabled when address is problematic and user would create/confirm order */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  onClick={handleEndCall}
                  disabled={
                    saving ||
                    (showAddressProblematic && (orderItems.length > 0 || !!orderNeedingConfirmation))
                  }
                  className="w-full px-4 py-2 bg-[#264a3e] text-white rounded-md hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 inline mr-2 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      {t('leads.endCall')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete order item confirmation */}
      {pendingDeleteItemIndex !== null && orderItems[pendingDeleteItemIndex] && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('callCenter.removeItemConfirmTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('callCenter.removeItemConfirmMessage', { name: orderItems[pendingDeleteItemIndex].productName })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDeleteItemIndex(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const idx = pendingDeleteItemIndex;
                  const item = orderItems[idx];
                  if (item) {
                    if (item.isSpecialOffer && item.specialOfferId) {
                      const newSelectedOffers = new Map(selectedOffers);
                      newSelectedOffers.delete(item.specialOfferId);
                      setSelectedOffers(newSelectedOffers);
                      if (selectedOffer && (selectedOffer.id === item.specialOfferId || selectedOffer._id === item.specialOfferId)) {
                        setSelectedOffer(null);
                        setSelectedOfferProducts([]);
                      }
                      updateOrderItemsFromSelectedOffers(newSelectedOffers);
                    } else {
                      setOrderItems(orderItems.filter((_, i) => i !== idx));
                    }
                  }
                  setPendingDeleteItemIndex(null);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                {t('callCenter.removeItemConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject order (order needing confirmation) confirmation */}
      {showRejectOrderModal && orderNeedingConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('callCenter.rejectOrderConfirmTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('callCenter.rejectOrderConfirmMessage')}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !rejectingOrder && setShowRejectOrderModal(false)}
                disabled={rejectingOrder}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleRejectOrderConfirm}
                disabled={rejectingOrder}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {rejectingOrder ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('callCenter.rejectOrderConfirmButton')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm order (order needing confirmation) confirmation */}
      {showConfirmOrderModal && orderNeedingConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('callCenter.confirmOrderConfirmTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('callCenter.confirmOrderConfirmMessage')}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !confirmingOrder && setShowConfirmOrderModal(false)}
                disabled={confirmingOrder}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmOrderConfirm}
                disabled={confirmingOrder}
                className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1e3a32] disabled:opacity-50 flex items-center gap-2"
              >
                {confirmingOrder ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('callCenter.confirmOrderConfirmButton')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* End Call Modal */}
      {showEndCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('common.endCallSave')}</h3>
              <button
                onClick={() => setShowEndCallModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {leadData && (
              <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white shadow-sm">
                <div className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#264a3e]/10">
                      <User className="h-5 w-5 text-[#264a3e]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">{leadData.name}</p>
                      {leadData.phone && (
                        <p className="text-sm text-gray-600">{formatPhoneWithCountryCode(leadData.phone, "rs")}</p>
                      )}
                    </div>
                  </div>
                  <div className="my-3 h-px bg-gray-200" />
                  <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Address</p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {getLeadDisplayAddress(leadData)}
                    </p>
                  </div>
                  {generalNotes && (
                    <>
                      <div className="my-3 h-px bg-gray-200" />
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{t('leads.generalNotes')}</p>
                        <p className="mt-0.5 text-sm text-gray-700 line-clamp-2 leading-relaxed">{generalNotes}</p>
                      </div>
                    </>
                  )}
                  {healthInfo.length > 0 && (
                    <>
                      <div className="my-3 h-px bg-gray-200" />
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{t('leads.healthInfo')}</p>
                        <p className="mt-0.5 text-sm text-gray-700">{healthInfo.length} {t('common.notes')}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.callOutcome')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={callNotes.callOutcome || ""}
                  onChange={(e) => {
                    const outcome = e.target.value as CallNotes["callOutcome"];
                    setCallNotes((prev) => ({
                      ...prev,
                      callOutcome: outcome,
                      notInterestedReason: outcome === "not_interested" ? prev.notInterestedReason : "",
                      ...(outcome === "callback" && !prev.followUpHours && leadData?.bestTimeToCall
                        ? { followUpHours: leadData.bestTimeToCall }
                        : {}),
                    }));
                  }}
                  required
                  disabled={orderItems.length > 0 && (callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t('common.select')}</option>
                  <option value="not_interested">{t('leads.notInterested')}</option>
                  <option value="callback">{t('leads.callback')}</option>
                  <option value="no_answer">{t('leads.noAnswer')}</option>
                  {(orderItems.length > 0 || endCallModalFromCreateOrder) && (
                    <>
                      <option value="successful">{t('leads.successful')}</option>
                      <option value="successful_with_confirmation">{t('leads.successfulWithConfirmation')}</option>
                    </>
                  )}
                  <option value="busy_line">{t('leads.busyLine')}</option>
                  <option value="wrong_number">{t('leads.wrongNumber')}</option>
                  <option value="unreachable">{t('leads.unreachable')}</option>
                  <option value="number_doesnt_exist">{t('leads.numberDoesntExist')}</option>
                  <option value="other">{t('leads.other')}</option>
                </select>
              </div>

              {callNotes.callOutcome === "not_interested" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('leads.notInterestedReason')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={callNotes.notInterestedReason || ""}
                    onChange={(e) => setCallNotes({ ...callNotes, notInterestedReason: e.target.value as NotInterestedReason })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="do_not_call_again">{t('leads.notInterestedReasonDoNotCallAgain')}</option>
                    <option value="no_money">{t('leads.notInterestedReasonNoMoney')}</option>
                    <option value="still_has_product">{t('leads.notInterestedReasonStillHasProduct')}</option>
                    <option value="product_doesnt_help">{t('leads.notInterestedReasonProductDoesntHelp')}</option>
                    <option value="because_of_death">{t('leads.notInterestedReasonBecauseOfDeath')}</option>
                    <option value="orders_for_someone_else">{t('leads.notInterestedReasonOrdersForSomeoneElse')}</option>
                    <option value="not_interested_not_interested">{t('leads.notInterestedReasonNotInterested')}</option>
                    <option value="other">{t('leads.notInterestedReasonOther')}</option>
                  </select>
                </div>
              )}

              {callNotes.callOutcome !== "callback" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.callNotes')}</label>
                  <textarea
                    value={callNotes.notes}
                    onChange={(e) => setCallNotes({ ...callNotes, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                    placeholder={t('common.enterNotes')}
                  />
                </div>
              )}

              {callNotes.callOutcome === "callback" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.followUpDate')}</label>
                    <input
                      type="date"
                      value={callNotes.followUpDate || ""}
                      onChange={(e) => setCallNotes({ ...callNotes, followUpDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('leads.bestTimeToCall')}</label>
                    <CustomTimePicker
                      value={callNotes.followUpHours || ""}
                      onChange={(v) => setCallNotes({ ...callNotes, followUpHours: v })}
                      placeholder={t('common.optional')}
                      compact
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.followUpNotes')}</label>
                    <textarea
                      value={callNotes.followUpNotes || ""}
                      onChange={(e) => setCallNotes({ ...callNotes, followUpNotes: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                      placeholder={t('common.notesForFollowUp')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('callCenter.maybeInterested')}</label>
                    <select
                      value={callNotes.followUpPotentialProduct || ""}
                      onChange={(e) => setCallNotes({ ...callNotes, followUpPotentialProduct: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e]"
                      title={t('common.optional')}
                    >
                      <option value="">{t('common.optional')}</option>
                      {products
                        .filter((p) => p.type !== "bundle")
                        .map((p) => (
                          <option key={p.id} value={p.name}>
                            {p.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {showAddressProblematic && (orderItems.length > 0 || !!orderNeedingConfirmation) && (
              <p className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {t('callCenter.fixAddressToCreateOrder')}
              </p>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleEndCallAndExit}
                disabled={
                  saving ||
                  !callNotes.callOutcome ||
                  (callNotes.callOutcome === "not_interested" && !callNotes.notInterestedReason) ||
                  ((callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation") && orderItems.length > 0 && needsConfirmation && !confirmOnDate) ||
                  (showAddressProblematic &&
                    (callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation") &&
                    (orderItems.length > 0 || !!orderNeedingConfirmation))
                }
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.endOnly')}
              </button>
              <button
                onClick={() => handleSaveAndEndCall(true)}
                disabled={
                  saving ||
                  !callNotes.callOutcome ||
                  (callNotes.callOutcome === "not_interested" && !callNotes.notInterestedReason) ||
                  ((callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation") && orderItems.length > 0 && needsConfirmation && !confirmOnDate) ||
                  (showAddressProblematic &&
                    (callNotes.callOutcome === "successful" || callNotes.callOutcome === "successful_with_confirmation") &&
                    (orderItems.length > 0 || !!orderNeedingConfirmation))
                }
                className="px-4 py-2 bg-[#264a3e] text-white rounded-md hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {saving ? t('common.saving') : t('common.saveNextCall')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadProcessingPage;
